// @ts-nocheck
// --- Audit Rules Engine (TypeScript) ---
// Note: @ts-nocheck is used because this 1400-line file has 39+ rule check functions
// with complex inferred types. Full typing is planned for phase 2.
import type { AccountRow, ResultRow, AuditFinding, AuditRule, AuditResult, SystemConfig, PreviousYearData, VatMonthlyRow, TransactionRow, CategorySummary } from './types';
import { t, getCurrentLang } from './translations';
import { Helpers, MONTH_SHORT, checkTransactionCoverage, resolvePeriod, periodEndDate, periodRange, isoToDotDate } from './helpers';
import { SYSTEMS } from './systems';
import { hasVerifiedEmptyTransactions } from './transaction-evidence';
import { allocateBalanceAge } from './balance-aging';

// LinkId relates documents (for example an invoice and its payment), not the
// lines of one voucher. Never certify document-level checks from partial groups.
const voucherKey = (tx) => {
    const year = String(tx.date || '').slice(0, 4);
    if (Number(tx.transactionNo) > 0) return `${year}:no:${tx.transactionTypeId || ''}:${tx.transactionNo}`;
    if (tx.stampNo) return `${year}:stamp:${tx.stampNo}`;
    if (tx.bundleId) return `${year}:bundle:${tx.bundleId}`;
    return null;
};
const missingVoucherFinding = (transactions, titleKey) => {
    const missing = transactions.filter(tx => !voucherKey(tx)).length;
    return missing ? {
        type: 'info', title: t(titleKey), desc: t('voucher_ids_missing', missing, transactions.length),
        details: [], specialType: 'rule_not_checked',
    } : null;
};

// Opposite VAT accounts may legitimately offset; a reversal within one account does not prove VAT.
const hasNetVatAccount = (txs: TransactionRow[], lo: number, hi: number): boolean => {
    const totals = new Map<number, number>();
    for (const tx of txs) if (tx.accountNo >= lo && tx.accountNo <= hi) totals.set(tx.accountNo, (totals.get(tx.accountNo) || 0) + tx.amount);
    return [...totals.values()].some(n => Math.abs(n) > 0.01);
};

// Nazwa miesiąca w języku biegu: klucz miesiąca z danych (1..12 albo 'Jan'..'Des') → month_short_n.
const monthName = (m) => {
    const n = parseInt(m, 10);
    const idx = (n >= 1 && n <= 12) ? n : MONTH_SHORT.findIndex(s => s.toLowerCase() === String(m || '').slice(0, 3).toLowerCase()) + 1;
    if (idx < 1) return String(m);
    const key = `month_short_${idx}`;
    const label = t(key);
    return label === key ? MONTH_SHORT[idx - 1] : label;
};

// Kontrakt karty (11.10.2026): „dlaczego", „sprawdziłem za Ciebie", pewność. Pola opcjonalne;
// puste nie wchodzą do znaleziska, stare raporty bez nich renderują się jak dotąd.
const cardContract = (r) => ({
    why: typeof r?.why === 'string' && r.why ? r.why : null,
    verified: Array.isArray(r?.verified) && r.verified.some(x => typeof x === 'string' && x)
        ? r.verified.filter(x => typeof x === 'string' && x) : null,
    confidence: ['confirmed', 'open', 'info'].includes(r?.confidence) ? r.confidence : null,
});

const notChecked = (titleKey, reason = 'audit_scope_unchecked') => ({
    type: 'info', title: t(titleKey), desc: t(reason), details: [], specialType: 'rule_not_checked', reason,
});

// Mapa reguł → klucze tłumaczeń (user-friendly nazwy)
export const RULE_LABELS: Record<string, string> = {
    saldo_1530: 'rule_saldo_1530',
    personalkostnader_negative: 'rule_personalkostnader_negative',
    arsregnskap_mangler: 'rule_arsregnskap_mangler',
    financial_result: 'rule_financial_profit',
    neg_assets: 'rule_neg_assets',
    pos_liabilities: 'rule_pos_liabilities',
    skattetrekk_2610_balance: 'rule_skattetrekk_2610_balance',
    skattetrekk_1950_balance: 'rule_skattetrekk_1950_balance',
    financial_income_sign_80xx: 'rule_financial_income_sign_80xx',
    financial_cost_sign_815x_816x: 'rule_financial_cost_sign_815x_816x',
    periodisering_2900_balance: 'rule_periodisering_2900_balance',
    mva_registered_no_vat_postings: 'rule_mva_registered_no_vat_postings',
    high_1571: 'rule_high_1571',
    mileage_7100: 'rule_7100',
    rent_6301: 'rule_6301_no_rent',
    conflict_7791_7799: 'rule_7791_7799',
    conflict_7790_7798: 'rule_7790_7798',
    conflict_4000_4300: 'rule_4000_4300',
    amortization_12xx: 'rule_amort_12xx',
    balance_control: 'rule_balanse_ok',
    neg_equity_2050: 'rule_neg_equity',
    negative_cash_1900: 'rule_kasse_neg',
    mva_reconciliation: 'rule_mva',
    private_car_7000: 'rule_bil',
    wrong_sign_costs: 'rule_wrong_sign_costs',
    anomalies_6xxx: 'rule_anomalies_6xxx',
    high_6540: 'rule_high_6540',
    salary_sign: 'rule_salary_sign',
    missing_5920: 'rule_missing_5920',
    skattetrekk: 'rule_skattetrekk',
    aga_5400: 'rule_aga',
    feriepenger: 'rule_feriepenger',
    otp_5930: 'rule_otp',
    periodisering: 'rule_period_label',
    kundefordringer_vs_omsetning: 'rule_kundefordringer',
    revisjonsplikt: 'rule_revisjon',
    revisjon_fravalg_mulig: 'rule_revisjon_fravalg_mulig',
    // skattetrekk_bank: WYŁĄCZONA od 2026
    likviditetsgrad: 'rule_likviditet',
    leverandorgjeld_ratio: 'rule_levgjeld',
    husleie_consistency: 'rule_husleie',
    negativ_bank: 'rule_neg_bank',
    aksjonaerlan_skatt: 'rule_aksjonaerlan',
    utbytte_2800: 'rule_utbytte_2800',
    no_accountant: 'rule_no_accountant',
    lonn_vs_aga: 'rule_lonn_aga',
    // leasing_6440: removed — leasing nie musi być miesięczny
    banklan_8150: 'rule_banklan_8150',
    salary_periodisering: 'rule_salary_periodisering',
    yoy_equity_decline: 'rule_yoy_equity_decline',
    yoy_debt_increase: 'rule_yoy_debt_increase',
    mva_period_check: 'rule_mva_period',
    mva_not_registered_vat_postings: 'rule_mva_not_registered_vat_postings',
    dead_months: 'rule_dead_months',
    cost_spike_extended: 'rule_cost_spike_ext',
    // Phase 6: Transaction-based rules
    duplicate_entries: 'rule_duplicates',
    duplicate_sales_invoices: 'rule_duplicate_sales',
    mva_per_voucher: 'rule_mva_voucher',
    mva_sales_voucher: 'rule_mva_sales',
    invoice_numbering: 'rule_invoice_numbering',
    varelager_check: 'rule_varelager',
    reverse_periodisering: 'rule_reverse_period',
    project_pnl_summary: 'rule_project_pnl',
    // v40: integralność importu + ciągłość IB/UB + prosjektregnskap (Thomas/Datamynt + Patrycja)
    yoy_ib_ub_continuity: 'rule_ib_ub_continuity',
    prior_year_not_closed: 'rule_prior_year_not_closed',
    import_integrity: 'rule_import_integrity',
    prosjektregnskap_terskel: 'rule_prosjektregnskap',
};

// Category badge labels
export const CATEGORY_BADGE_LABELS: Record<string, string> = {
    compliance: 'category_label_compliance',
    balance: 'category_label_balance',
    financial: 'category_label_financial',
    result: 'category_label_result',
    analysis: 'category_label_analysis',
};
export const CATEGORY_BADGE_COLORS: Record<string, string> = {
    compliance: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
    balance: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
    financial: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
    result: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
    analysis: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
};

const isKnownNotMvaRegistered = (config: any) => config?.brregMvaRegistered === false;
const validCivilDate = (value: unknown): value is string => typeof value === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value))
    && new Date(value).toISOString().slice(0, 10) === value;
const hasVatCode = (tx: any) => {
    if (!tx) return false;
    if (typeof tx.vatEvidence === 'boolean') return tx.vatEvidence;
    const raw = tx.vatCode ?? tx.VatCode ?? tx.vat_code;
    if (raw === null || raw === undefined || raw === '') return false;
    const normalized = String(raw).trim().toLowerCase();
    return normalized !== '' && normalized !== '0' && normalized !== 'none' && normalized !== 'null';
};
const isVatAccountNo = (accNo: any) => {
    const n = Number(accNo);
    return Number.isFinite(n) && n >= 2700 && n <= 2749;
};

// Konto 5000, jeśli firma jest pracodawcą w okresie: DODATNI koszt pensji, nie dowolna kwota.
// Samo storno (ujemne 5000) to nie pensja. Wzór i pomiar na korpusie 438: RAPPORTER e371bc39 (D-210).
// Zakres świadomie zostaje przy koncie 5000: szerszy (5000-5299) dołożyłby flagi, których na
// portfelu AUDYTORA nikt nie zmierzył.
const wageAccount = (resultat: any[], config: any) => {
    const acc = (resultat || []).find(r => r.accNum === 5000);
    if (!acc) return null;
    const cost = (config?.costsArePositive === false ? -1 : 1) * (Number(acc.sum) || 0);
    return cost >= 1 ? acc : null;
};

const isBankAccount = (accNum: number) => accNum >= 1920 && accNum <= 1999;

// Reszta wyniku roku poprzedniego, poniżej której różnicę uznajemy za zaokrąglenie.
// Wartość z audytu sald 24.09.2026 (najmniejsza potwierdzona reszta: 2 339 kr).
const PRIOR_YEAR_REMAINDER_TOL_KR = 100;
// Od jakiego udziału dyspozycji w wyniku uznajemy, że journal roczny został zaksięgowany.
const PRIOR_YEAR_MIN_DISPOSITION_SHARE = 0.25;

// Terskel for prosjektregnskap, bokføringsforskriften §8-1-3: 5 × grunnbeløp eks. mva.
// Verdi for 2026 ≈ 650 800 kr (satt juni 2026). Grunnbeløp justeres ~1. mai hvert år
// → OPPDATER denne årlig (eller overstyr per klient via config.prosjektregnskapThreshold).
const PROSJEKTREGNSKAP_5G = 650800;

// Tolerancja niezbilansowania bilagu w import_integrity. 50 kr (nie 1 kr) — walidacja na
// żywych danych 24SO (6 firm, 2026-06-13) pokazała ogon ~1-6% bilagów z drobnymi różnicami
// na kontach finansowych (8160 agio, zaokrąglenia płatności) rzędu 18-28 kr. To NIE jest
// zgubiona linia (ta jest rzędu setek+), więc nie alarmujemy poniżej 50 kr.
const IMPORT_INTEGRITY_TOL_KR = 50;

export const RULE_CATEGORIES: { id: string; labelKey: string }[] = [
    { id: 'financial', labelKey: 'settingsCategory_financial' },
    { id: 'balance', labelKey: 'settingsCategory_balance' },
    { id: 'result', labelKey: 'settingsCategory_result' },
    { id: 'compliance', labelKey: 'settingsCategory_compliance' },
    { id: 'analysis', labelKey: 'settingsCategory_analysis' },
];

const agedBalanceFinding = (saldo, account, sign, config, transactions, titleKey, inclusive = false) => {
    const row = saldo.find(a => a.accNum === account);
    if (!row || !Number.isFinite(row.ub) || (inclusive ? Math.abs(row.ub) < 1 : Math.abs(row.ub) <= 1)) return null;
    const evidence = allocateBalanceAge(row, sign, config, transactions);
    if (evidence.status === 'latest_only') return null;
    const labels = evidence.allocations.map(a => ({ ...a, label: a.kind === 'ib' ? `IB ${a.period}`
        : `${a.period.slice(5)}.${a.period.slice(0, 4)}` }));
    let desc = evidence.status === 'atypical' ? t('balance_age_atypical', account, Helpers.formatMoney(row.ub))
        : evidence.status === 'unavailable' ? t('balance_age_unavailable', account, Helpers.formatMoney(row.ub))
        : t('balance_age_older', account, Helpers.formatMoney(row.ub), labels.map(a => `${a.label}: ${Helpers.formatMoney(a.amount)}`).join('; '));
    if (!transactions?.length) desc += ' ' + t('balance_age_no_transactions');
    const actionKey = evidence.status === 'atypical' ? 'action_balance_age_atypical'
        : evidence.status === 'unavailable' ? 'action_balance_age_unavailable' : null;
    return { type: 'warning', title: t(titleKey), desc, evidence, ...(actionKey ? { actionKey } : {}),
        details: [row, ...labels.map(a => ({ accNum: account, name: a.label, ub: a.amount }))] };
};

export const AUDIT_RULES: AuditRule[] = [
    {
        id: 'saldo_1530', category: 'balance', systems: 'all', requires: 'saldo',
        check: (saldo, _resultat, config, _previous, _vat, transactions = []) =>
            agedBalanceFinding(saldo, 1530, 1, config, transactions, 'rule_saldo_1530', true),
    },
    {
        id: 'personalkostnader_negative', category: 'analysis', systems: 'all', requires: 'resultat',
        check: (_saldo, resultat, config) => {
            const sign = config?.costsArePositive === false ? -1 : 1;
            const sourceRows = resultat.filter(r => r.accNum >= 5000 && r.accNum <= 5999);
            if (!sourceRows.length || sourceRows.some(r => !Number.isFinite(r.sum))) return null;
            const rows = sourceRows.map(r => ({ ...r, sum: sign * r.sum }));
            const total = rows.reduce((sum, r) => sum + r.sum, 0);
            if (total >= -1) return null;
            const details = [...rows].sort((a, b) => Math.abs(b.sum) - Math.abs(a.sum) || a.accNum - b.accNum).slice(0, 3);
            return { type: 'warning', title: t('rule_personalkostnader_negative'), details,
                desc: t('rule_personalkostnader_negative_desc', Helpers.formatMoney(total),
                    details.map(r => `${r.accNum}: ${Helpers.formatMoney(r.sum)}`).join('; ')), evidence: { total } };
        },
    },
    {
        id: 'arsregnskap_mangler', category: 'compliance', systems: 'all', requires: 'metadata',
        check: (_saldo, _resultat, config) => {
            const accounts = config?.brreg?.accounts;
            const orgNr = String(config?.orgNr || '').replace(/\s/g, '');
            const auditDate = config?.auditDate;
            if (!/^\d{9}$/.test(orgNr) || config?.brreg?.orgNr !== orgNr || accounts?.orgNr !== orgNr
                || !['ok', 'empty'].includes(accounts?.status) || !validCivilDate(accounts?.founded) || !validCivilDate(auditDate)) return null;
            const year = Number(auditDate.slice(0, 4)), requiredYear = year - 1;
            const lastYear = accounts.lastYear;
            if (lastYear !== null && (!Number.isInteger(lastYear) || lastYear < 1)) return null;
            if (accounts.founded > `${requiredYear}-12-31` || auditDate <= `${year}-07-31`
                || (lastYear !== null && lastYear >= requiredYear)) return null;
            return { type: 'warning', title: t('rule_arsregnskap_mangler'), source: 'brreg', details: [],
                desc: t('rule_arsregnskap_mangler_desc', requiredYear, lastYear ?? t('arsregnskap_no_year'), year),
                evidence: { orgNr, auditDate, requiredYear, lastYear, founded: accounts.founded,
                    source: `https://data.brreg.no/regnskapsregisteret/regnskap/${orgNr}` } };
        },
    },
    // --- WYNIK FINANSOWY ---
    {
        id: 'financial_result',
        category: 'financial',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const totalSum = resultat.reduce((s, r) => s + r.sum, 0);
            const isProfit = config.costsArePositive ? totalSum < 0 : totalSum > 0;
            // 11.10.2026 (decyzja ownera): kwota na karcie w znaku czytelnika, zysk dodatni, strata
            // ujemna, niezależnie od konwencji źródła (Finago trzyma przychody ze znakiem minus,
            // więc „OVERSKUDD −916 953,78" wyglądało jak sprzeczność).
            const shown = config.costsArePositive ? -totalSum : totalSum;
            return {
                type: isProfit ? 'success' : 'info',
                title: isProfit ? t('rule_financial_profit') : t('rule_financial_loss'),
                desc: t('rule_financial_desc', Helpers.formatMoney(shown)),
                source: 'resultat', specialType: 'financial_result'
            };
        }
    },

    // --- SALDOBALANSE ---
    {
        id: 'neg_assets',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config) => {
            // Wyklucz konta amortyzacji zbiorczej (12x9: 1209, 1219, ..., 1299) + 1580 tapsavsetning
            // Konta kończące się na 9 w zakresie 1200-1299 to akkumulert avskriving — z natury ujemne
            const isAccumulatedDepr = (acc) => acc >= 1200 && acc <= 1299 && acc % 10 === 9;
            // Rozpoznawaj amortyzację po nazwie tylko w grupie środków trwałych 12xx.
            const isDeprByName = (a) => {
                if (a.accNum < 1200 || a.accNum > 1299) return false;
                const n = (a.accName || a.name || '').toLowerCase();
                return n.includes('avskrivning') || n.includes('avskriving') || n.includes('akkumulert')
                    || n.includes('amortisering') || /\bavskr\./i.test(n);
            };
            // Wyłącz jawnie opisane odpisy wartości zapasów w grupie 14xx.
            const isInventoryWriteOff = (a) => a.accNum >= 1400 && a.accNum <= 1499
                && /\bukurans\b/i.test(String(a.accName || a.name || ''));
            // Bank (1920-1999) zgłasza negativ_bank jako ostrzeżenie. Audyt sald 24.09.2026:
            // 21 flag ujemnego banku, 5 potwierdzonych jako błąd ksiąg, reszta to timing i kassekreditt.
            const found = saldo.filter(a => a.accNum >= 1000 && a.accNum <= 1999 && a.accNum !== 1580 && !isBankAccount(a.accNum) && !isAccumulatedDepr(a.accNum) && !isDeprByName(a) && !isInventoryWriteOff(a) && a.ub < -5);
            if (found.length === 0) return null;
            // Kontrakt karty (Knu 11.10.2026, 1531 −938 444 obok 1530 +938 444): konto z tej samej
            // dziesiątki o przeciwnym saldzie oznacza odwrócenie zaksięgowane na innym koncie.
            // „Potwierdzone" tylko dla pary przychodu niezafakturowanego 153x i kwoty > 1 000 kr
            // (przegląd 11.10.2026: 1510 −25 000 obok 1519 +25 000 to przypadek, nie lustro).
            const mirrors = found.map(a => {
                const twin = saldo.find(b => b.accNum !== a.accNum && Math.floor(b.accNum / 10) === Math.floor(a.accNum / 10) && Math.abs((b.ub || 0) + a.ub) <= 1 && Math.abs(a.ub) > 1000);
                if (!twin) return null;
                const strong = a.accNum >= 1530 && a.accNum <= 1539 && twin.accNum >= 1530 && twin.accNum <= 1539;
                return { text: t('neg_assets_mirror', a.accNum, twin.accNum, Helpers.formatMoney(Math.abs(a.ub))), strong };
            }).filter(Boolean);
            const period = resolvePeriod(config);
            return {
                type: 'error', title: t('rule_neg_assets'),
                desc: period ? t('rule_neg_assets_desc_at', periodEndDate(period)) : t('rule_neg_assets_desc'),
                details: found,
                why: t('neg_assets_why'),
                verified: mirrors.length ? mirrors.map(m => m.text) : [t('neg_assets_no_mirror')],
                // Zgodność kwot wskazuje przyczynę, ale jej nie dowodzi bez bilagów (Codex P2): „do wyjaśnienia".
                confidence: 'open',
            };
        }
    },
    {
        id: 'pos_liabilities',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear) => {
            const issues = [];
            const previousSaldo = previousYear?.saldo || [];
            const payrollBridgeAccounts = saldo.filter(a => [2780, 2785].includes(a.accNum) && a.ub > 1);
            if (payrollBridgeAccounts.length > 0) {
                const previous2785 = previousSaldo.find(a => a.accNum === 2785 && Math.abs(a.ub) > 1);
                issues.push({
                    type: 'info',
                    title: t('rule_pos_liabilities_payroll_bridge'),
                    desc: t('rule_pos_liabilities_payroll_bridge_desc'),
                    details: previous2785 ? [...payrollBridgeAccounts, { ...previous2785, name: `${previous2785.name || previous2785.accName || '2785'} (31.12 forrige år)` }] : payrollBridgeAccounts,
                });
            }
            const companyForm = String(config?.companyForm || config?.clientForm || '').toLowerCase();
            const isEnk = companyForm.includes('enk');
            const found = saldo.filter(a => {
                // 03.11.2026: dodatnie saldo 2060 i 2070 w ENK to prywatne wypłaty właściciela, nie błąd zobowiązań.
                if (isEnk && [2060, 2070].includes(a.accNum)) return false;
                const isVat = a.accNum >= 2700 && a.accNum <= 2740;
                // Skattetrekk (2600-2699) naturalnie zmienia znak w ciągu roku — nie flagować (feedback Marzena 2026-03-23)
                const isSkattetrekk = a.accNum >= 2600 && a.accNum <= 2699;
                const isPayrollBridge = [2780, 2785].includes(a.accNum);
                const isPeriodizationClearing = a.accNum >= 2900 && a.accNum <= 2999;
                const isDebitNature = [2030, 2035, 2036, 2039, 2050, 2080, 2510, 2540, 2800].includes(a.accNum);
                return a.accNum >= 2000 && a.accNum <= 2999 && !isDebitNature && !isVat && !isSkattetrekk && !isPayrollBridge && !isPeriodizationClearing && a.ub > 1;
            });
            const periodisering = saldo.filter(a => a.accNum >= 2901 && a.accNum <= 2999 && a.ub > 1);
            if (periodisering.length > 0) {
                issues.push({
                    type: 'info',
                    title: t('rule_pos_liabilities_periodization'),
                    desc: t('rule_pos_liabilities_periodization_desc'),
                    details: periodisering,
                });
            }
            // Feedback Justyna Nastarowska 2026-05-22: positive balance on 2xxx can be useful
            // (e.g. 2960 periodisering), but is not always a hard accounting error.
            if (found.length > 0) {
                issues.push({ type: 'warning', title: t('rule_pos_liabilities'), desc: t('rule_pos_liabilities_desc'), details: found });
            }
            if (issues.length === 0) return null;
            return issues.length === 1 ? issues[0] : issues;
        }
    },
    {
        id: 'skattetrekk_2610_balance',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, _resultat, config, _previous, _vat, transactions = []) =>
            agedBalanceFinding(saldo, 2610, -1, config, transactions, 'rule_skattetrekk_2610_balance'),
    },
    {
        id: 'skattetrekk_1950_balance',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc = saldo.find(a => a.accNum === 1950 && Math.abs(a.ub) > 1);
            // Na start ostrzeżenie, nie błąd (decyzja właściciela 29.10.2026).
            return acc ? { type: 'warning', title: t('rule_skattetrekk_1950_balance'),
                desc: t('rule_skattetrekk_1950_balance_desc'), details: [acc] } : null;
        }
    },
    {
        id: 'financial_income_sign_80xx',
        category: 'result',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const found = resultat.filter(a => [8050, 8060].includes(a.accNum) && Math.abs(a.sum) >= 500
                && (config.costsArePositive ? a.sum > 0 : a.sum < 0));
            return found.length ? { type: 'warning', title: t('rule_financial_income_sign_80xx'),
                desc: t('rule_financial_income_sign_80xx_desc'), details: found, source: 'resultat' } : null;
        }
    },
    {
        id: 'financial_cost_sign_815x_816x',
        category: 'result',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const found = resultat.filter(a => a.accNum >= 8150 && a.accNum <= 8169 && Math.abs(a.sum) >= 500
                && (config.costsArePositive ? a.sum < 0 : a.sum > 0));
            return found.length ? { type: 'warning', title: t('rule_financial_cost_sign_815x_816x'),
                desc: t('rule_financial_cost_sign_815x_816x_desc'), details: found, source: 'resultat' } : null;
        }
    },
    {
        id: 'periodisering_2900_balance',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, _resultat, config, _previous, _vat, transactions = []) =>
            agedBalanceFinding(saldo, 2900, -1, config, transactions, 'rule_periodisering_2900_balance'),
    },
    {
        id: 'high_1571',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc = saldo.find(a => a.accNum === 1571);
            return acc && acc.ub > 50000 ? { type: 'warning', title: t('rule_high_1571'), desc: t('rule_high_1571_desc'), details: [acc] } : null;
        }
    },
    {
        id: 'mileage_7100',
        category: 'balance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat) => {
            const acc = saldo.find(a => a.accNum === 7100);
            if (!acc || Math.abs(acc.ub) < 1) return null;
            // Sprawdź czy firma ma wynagrodzenia (5000) i skattetrekk (2600) = system lønn
            const hasLonn = saldo.some(a => a.accNum === 5000 && Math.abs(a.ub) > 0) || (resultat && resultat.some(r => r.accNum === 5000 && Math.abs(r.sum) > 0));
            const hasSkattetrekk = saldo.some(a => a.accNum >= 2600 && a.accNum <= 2699 && Math.abs(a.ub) > 0);
            const hasLonnSystem = hasLonn && hasSkattetrekk;
            return { type: 'info', title: t('rule_7100'), desc: t('rule_7100_desc', Helpers.formatMoney(acc.ub), hasLonnSystem), details: [acc] };
        }
    },
    {
        id: 'rent_6301',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc6301 = saldo.find(a => a.accNum === 6301);
            if (!acc6301 || Math.abs(acc6301.ub) === 0) return null;
            // Sprawdź czy istnieją jakiekolwiek konta husleie (6300-6309, poza 6301)
            const rentAccounts = saldo.filter(a => a.accNum >= 6300 && a.accNum <= 6309 && a.accNum !== 6301 && Math.abs(a.ub) > 0);
            if (rentAccounts.length === 0) return { type: 'error', title: t('rule_6301_no_rent'), desc: t('rule_6301_no_rent_desc'), details: [acc6301] };
            const sumRent = rentAccounts.reduce((s, a) => s + Math.abs(a.ub), 0);
            if (Math.abs(acc6301.ub) > sumRent) return { type: 'warning', title: t('rule_6301_high'), desc: t('rule_6301_high_desc'), details: [...rentAccounts, acc6301] };
            return null;
        }
    },
    {
        id: 'conflict_7790_7798',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc7790 = saldo.find(a => a.accNum === 7790 && a.ub !== 0);
            const acc7798 = saldo.find(a => a.accNum === 7798 && a.ub !== 0);
            return acc7790 && acc7798 ? [{ type: 'warning', title: t('rule_7790_7798'),
                desc: t('rule_7790_7798_desc'), details: [acc7790, acc7798] }] : null;
        }
    },
    {
        id: 'conflict_7791_7799',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat = []) => {
            // API keeps 7xxx in resultat; CSV can also supply their actual UB.
            // A result sum proves period movement, never a closing balance.
            const usedAccount = n => {
                const balance = saldo.find(a => a.accNum === n);
                const result = resultat.find(a => a.accNum === n);
                if (Math.abs(balance?.change) >= 1 || Math.abs(balance?.ub) >= 1) return balance;
                return Math.abs(result?.sum) >= 1 ? result : null;
            };
            const acc7791 = usedAccount(7791);
            const acc7799 = usedAccount(7799);
            const issues = [];
            if (acc7791 && acc7799) issues.push({ type: 'warning', title: t('rule_7791_7799'), desc: t('rule_7791_7799_desc'), details: [acc7791, acc7799] });
            const high7791 = saldo.find(a => a.accNum === 7791 && a.ub > 30000);
            if (high7791) issues.push({ type: 'warning', title: t('rule_high_7791'), desc: t('rule_high_7791_desc'), details: [high7791] });
            return issues.length > 0 ? issues : null;
        }
    },
    {
        id: 'conflict_4000_4300',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            // Zmienione na info — wiele firm legalnie używa obu kont jednocześnie (feedback Marzena 2026-03-23)
            const a = saldo.find(a => a.accNum === 4000 && a.ub !== 0);
            const b = saldo.find(a => a.accNum === 4300 && a.ub !== 0);
            return a && b ? { type: 'info', title: t('rule_4000_4300'), desc: t('rule_4000_4300_desc'), details: [a, b] } : null;
        }
    },
    {
        id: 'amortization_12xx',
        category: 'balance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat, config) => {
            const assets = saldo.filter(a => a.accNum >= 1200 && a.accNum <= 1298 && (Math.abs(a.ub) > 1 || Math.abs(a.change) > 1));
            const issues = [];
            if (assets.length > 0) {
                // Grupuj konta per klasa (12x0-12x9) — feedback Przemek: "powinien sprawdzać jako całość"
                // np. 1200-1209, 1210-1219, 1230-1239, 1290-1299
                const groups = {};
                assets.forEach(a => {
                    const group = Math.floor(a.accNum / 10) * 10; // 1200, 1210, 1230, etc.
                    if (!groups[group]) groups[group] = [];
                    groups[group].push(a);
                });
                // Sprawdź amortyzację zbiorczą (12x9/1299). Ruch na 12x9 oznacza bieżącą amortyzację,
                // a saldo 12xx + 1299 ~= 0 oznacza środek trwały w pełni umorzony (feedback Patrycja 2026-05-21).
                const accumDepr = saldo.filter(a => a.accNum >= 1200 && a.accNum <= 1299 && String(a.accNum).endsWith('9') && (Math.abs(a.ub) > 1 || Math.abs(a.change) > 1));
                const currentAccumDepr = accumDepr.filter(a => Math.abs(a.change) > 1);
                const hasCurrentAccumDepr = currentAccumDepr.length > 0;
                const assetUb = assets.reduce((s, a) => s + a.ub, 0);
                const accumUb = accumDepr.reduce((s, a) => s + a.ub, 0);
                const fullyDepreciatedByBalance = assetUb > 1 && accumUb < -1 && Math.abs(assetUb + accumUb) <= 1;
                // Per grupa: pokaż sumę UB zamiast każdego konta osobno
                const groupedAssets = Object.entries(groups).map(([groupKey, accs]) => {
                    const groupAccs = accs as any[];
                    const totalUb = groupAccs.reduce((s, a) => s + a.ub, 0);
                    const totalChange = groupAccs.reduce((s, a) => s + a.change, 0);
                    // Sprawdź czy w tej grupie jest konto amortyzacji (kończy się na 9)
                    const groupDepr = saldo.find(a => a.accNum === Number(groupKey) + 9 && (Math.abs(a.ub) > 1 || Math.abs(a.change) > 1));
                    if (groupAccs.length === 1) {
                        return { ...groupAccs[0], hasAccumulatedDepreciation: hasCurrentAccumDepr || fullyDepreciatedByBalance || !!groupDepr };
                    }
                    // Skonsolidowany wiersz per grupa
                    return {
                        id: `${groupKey}-${Number(groupKey) + 9}`,
                        accNum: Number(groupKey),
                        name: `${groupAccs.map(a => a.accName || a.name || a.accNum).join(' + ')}`,
                        accName: t('amort_konto_group', groupKey, Number(groupKey) + 9, groupAccs.length),
                        ub: totalUb,
                        change: totalChange,
                        ib: totalUb - totalChange,
                        hasAccumulatedDepreciation: hasCurrentAccumDepr || fullyDepreciatedByBalance || !!groupDepr,
                        _subAccounts: groupAccs,
                    };
                });
                // 60xx to konta wynikowe — szukaj w resultat (nie saldo)
                const sum60xx = resultat.filter(r => r.accNum >= 6000 && r.accNum <= 6099).reduce((s, r) => s + Math.abs(r.sum), 0);
                // Kontrakt karty (Knu 11.10.2026, 1253 „Nowy zakup?"): czy w okresie są odpisy.
                issues.push({
                    type: 'info', title: t('rule_amort_12xx'), desc: t('rule_amort_12xx_desc'), details: groupedAssets, specialType: 'amortization',
                    why: t('amort_why'),
                    verified: [sum60xx > 0 ? t('amort_depr_in_period', Helpers.formatMoney(sum60xx)) : t('amort_no_depr')],
                    confidence: 'info',
                });
                const monthKeys = new Set(resultat.flatMap(r => r.months ? Object.keys(r.months) : []));
                const hasDecember = ['12', 'Des', 'Dec'].some(m => monthKeys.has(m));
                const isYearEnd = monthKeys.size === 0 || hasDecember || monthKeys.size >= 12;
                const companyForm = String(config?.companyForm || config?.clientForm || '').toLowerCase();
                const isEnk = companyForm.includes('enk') || companyForm.includes('enkeltpersonforetak');
                if (sum60xx === 0 && !hasCurrentAccumDepr && !fullyDepreciatedByBalance && !(isEnk && !isYearEnd)) {
                    issues.push({
                        type: isYearEnd ? 'error' : 'warning',
                        title: isYearEnd ? t('rule_amort_system_error') : t('rule_amort_partial_missing'),
                        desc: isYearEnd ? t('rule_amort_system_error_desc') : t('rule_amort_partial_missing_desc'),
                        evidence: {
                            source: 'amortization-ledger-v1',
                            assets: saldo.filter(a => a.accNum >= 1200 && a.accNum <= 1299)
                                .map(({ accNum, ib, ub, change }) => ({ accNum, ib, ub, change })),
                            depreciation: resultat.filter(r => r.accNum >= 6000 && r.accNum <= 6099)
                                .map(({ accNum, sum, months }) => ({ accNum, sum, months })),
                        },
                    });
                }
            }
            return issues.length > 0 ? issues : null;
        }
    },
    {
        id: 'balance_control',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat) => {
            const sumA = saldo.filter(a => a.accNum >= 1000 && a.accNum <= 1999).reduce((s, a) => s + a.ub, 0);
            const sumP = saldo.filter(a => a.accNum >= 2000 && a.accNum <= 2999).reduce((s, a) => s + a.ub, 0);
            // Jeśli saldo zawiera konta 3xxx-8xxx (pełna saldobalanse z wynikiem), sprawdź sum(ALL) = 0
            const hasPnL = saldo.some(a => a.accNum >= 3000);
            if (hasPnL) {
                const sumAll = saldo.reduce((s, a) => s + a.ub, 0);
                const diff = Math.abs(sumAll);
                if (diff > 1) return { type: 'error', title: t('rule_balanse_error'), evidence: { sumA, sumP, diff }, desc: t('rule_balanse_error_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP), Helpers.formatMoney(diff)) };
                return { type: 'success', title: t('rule_balanse_ok'), desc: t('rule_balanse_ok_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP)) };
            }
            // Tylko bilans (1xxx-2xxx) — uwzględnij wynik finansowy (resultat) jako brakującą pozycję
            const financialResult = (resultat && resultat.length > 0)
                ? resultat.reduce((s, r) => s + (r.sum || 0), 0)
                : 0;
            const rawDiff = sumA + sumP; // bez wyniku
            if (Math.abs(rawDiff) <= 1) return { type: 'success', title: t('rule_balanse_ok'), desc: t('rule_balanse_ok_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP)) };
            const adjustedDiff = Math.abs(rawDiff + financialResult); // z wynikiem
            // Feedback 2026-03-25: Różnica często = brakujące tilleggspostering (årsoppgjørsposteringer).
            // Wynik z resultat nie pokrywa różnicy bo nie ma jeszcze zamknięcia roku. Severity: warning (było error).
            // Feedback Artur 2026-05-22: GetAggregated does not always contain the full
            // balance-report total used by 24SO ("Sum egenkapital og gjeld"). Until RAPPORTER
            // fetches that total directly, do not show this check for partial periods.
            const resultMonths = new Set<string>();
            (resultat || []).forEach(r => Object.keys(r.months || {}).forEach(m => resultMonths.add(m)));
            if (adjustedDiff <= 1) return { type: 'success', title: t('rule_balanse_ok'), desc: t('rule_balanse_ok_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP)) };
            if (resultMonths.size < 12) return null;
            if (adjustedDiff > 1) return { type: 'info', title: t('rule_balanse_partial'), evidence: { sumA, sumP, adjustedDiff, financialResult }, desc: t('rule_balanse_partial_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP), Helpers.formatMoney(adjustedDiff)) };
            return { type: 'success', title: t('rule_balanse_ok'), desc: t('rule_balanse_ok_desc', Helpers.formatMoney(sumA), Helpers.formatMoney(sumP)) };
        }
    },
    // --- Varelager check — firma sprzedaje towary ale brak zapasów (tylko audyt na koniec roku) ---
    {
        id: 'varelager_check',
        category: 'balance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat) => {
            // Tylko przy audycie na koniec roku — sprawdź czy dane zawierają grudzień
            const sample = resultat.find(r => r.months);
            if (sample && sample.months) {
                const hasDecember = sample.months['12'] !== undefined || sample.months['Des'] !== undefined;
                if (!hasDecember) return null; // nie audyt na koniec roku
            }
            // Sprawdź czy firma ma koszty towarowe (4000-4999)
            const varekostnad = resultat.filter(r => r.accNum >= 4000 && r.accNum <= 4999 && Math.abs(r.sum) > 1000);
            if (varekostnad.length === 0) return null;
            // Sprawdź czy istnieje varelager (1400-1499) w saldo
            const varelager = saldo.filter(a => a.accNum >= 1400 && a.accNum <= 1499);
            const hasVarelager = varelager.some(a => Math.abs(a.ub) > 0);
            if (hasVarelager) return null;
            // Firma kupuje towary ale brak varelager → info (bez szczegółów kont)
            const totalVarekost = varekostnad.reduce((s, r) => s + Math.abs(r.sum), 0);
            return {
                type: 'info',
                title: t('rule_varelager'), evidence: { totalVarekost },
                desc: t('rule_varelager_desc', Helpers.formatMoney(totalVarekost)),
                details: [],
            };
        }
    },
    {
        id: 'neg_equity_2050',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc = saldo.find(a => a.accNum === 2050);
            return acc && acc.ub > 0 ? { type: 'info', title: t('rule_neg_equity'), desc: t('rule_neg_equity_desc', Helpers.formatMoney(acc.ub)), details: [acc] } : null;
        }
    },
    {
        id: 'negative_cash_1900',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc = saldo.find(a => a.accNum === 1900);
            return acc && acc.ub < 0 ? { type: 'error', title: t('rule_kasse_neg'), desc: t('rule_kasse_neg_desc', Helpers.formatMoney(acc.ub)), details: [acc] } : null;
        }
    },
    {
        id: 'mva_reconciliation',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear, vatMonthly) => {
            if (isKnownNotMvaRegistered(config)) return null;
            // Skip if vatMonthly available — mva_period_check handles MVA for API data
            if (vatMonthly?.some(a => isVatAccountNo(a.n))) return null;
            const vatAccounts = saldo.filter(a => a.accNum >= 2700 && a.accNum <= 2749);
            if (vatAccounts.length === 0) return null;
            const netMVA = vatAccounts.reduce((s, a) => s + a.ub, 0);
            if (Math.abs(netMVA) < 100) return null; // rozliczone — OK
            const utgaaende = vatAccounts.filter(a => a.accNum >= 2700 && a.accNum <= 2709).reduce((s, a) => s + a.ub, 0);
            const inngaaende = vatAccounts.filter(a => a.accNum >= 2710 && a.accNum <= 2719).reduce((s, a) => s + a.ub, 0);
            const oppgjor = vatAccounts.filter(a => a.accNum === 2740).reduce((s, a) => s + a.ub, 0);
            return { type: 'warning', title: t('rule_mva'), desc: t('rule_mva_desc', Helpers.formatMoney(utgaaende), Helpers.formatMoney(inngaaende), Helpers.formatMoney(netMVA)), details: vatAccounts };
        }
    },
    {
        id: 'mva_not_registered_vat_postings',
        category: 'compliance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!isKnownNotMvaRegistered(config)) return null;
            const saldoVat = (saldo || [])
                .filter(a => isVatAccountNo(a.accNum) && (Math.abs(Number(a.ub || 0)) > 1 || Math.abs(Number(a.change || 0)) > 1))
                .map(a => ({ ...a, sourceLabel: 'saldo' }));
            const txVat = (transactions || [])
                .filter(tx => isVatAccountNo(tx.accountNo) || hasVatCode(tx))
                .slice(0, 30)
                .map(tx => ({
                    accNum: tx.accountNo || '',
                    name: `${tx.date || '?'} | ${tx.stampNo || tx.linkId || tx.bundleId || tx.transactionId || 'bilag'} | ${tx.description || ''}`.trim(),
                    accName: hasVatCode(tx) ? `MVA-kode ${tx.vatCode}` : 'MVA-konto',
                    ub: Number(tx.amount || 0),
                    sourceLabel: 'transactions',
                }));
            const details = [...saldoVat, ...txVat].slice(0, 40);
            if (details.length === 0) return null;
            return {
                type: 'error',
                title: t('rule_mva_not_registered_vat_postings'),
                desc: t('rule_mva_not_registered_vat_postings_desc', details.length, config?.brreg?.name || config?.companyName || ''),
                details,
            };
        }
    },
    {
        id: 'mva_registered_no_vat_postings',
        category: 'compliance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions = []) => {
            if (config?.brregMvaRegistered !== true) return null;
            const hasSaldoActivity = saldo.some(a => a.accNum >= 2700 && a.accNum <= 2749
                && (Math.abs(a.change) > 1 || Math.abs(a.ub) > 1));
            const hasTransaction = (transactions || []).some(tx => tx.accountNo >= 2700 && tx.accountNo <= 2749);
            if (hasSaldoActivity || hasTransaction) return null;
            return {
                type: 'warning', title: t('rule_mva_registered_no_vat_postings'),
                desc: t('rule_mva_registered_no_vat_postings_desc', config?.brreg?.name || config?.companyName || ''),
                details: saldo.filter(a => a.accNum >= 2700 && a.accNum <= 2749),
            };
        }
    },
    {
        id: 'mva_period_check',
        category: 'compliance',
        systems: ['24seven'],
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions = []) => {
            if (isKnownNotMvaRegistered(config)) return null;
            if (!vatMonthly || vatMonthly.length === 0) return null;
            vatMonthly = vatMonthly.filter(a => isVatAccountNo(a.n));
            if (vatMonthly.length === 0) return null;
            const MN = ['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];
            const VAT_MI = [1, 3, 5, 7, 9, 11];
            const TL = ['1 (Jan\u2013Feb)', '2 (Mar\u2013Apr)', '3 (Mai\u2013Jun)', '4 (Jul\u2013Aug)', '5 (Sep\u2013Okt)', '6 (Nov\u2013Des)'];
            const available = Object.keys(vatMonthly[0]?.monthlyBalance || {});
            const hasChange = !!vatMonthly[0]?.monthlyChange;
            const explicitPeriod = vatMonthly[0]?.periodStartMonth;
            const partialTerm = explicitPeriod && (explicitPeriod % 2 === 0 || available.length % 2 !== 0);
            const acc2740 = vatMonthly.find(a => a.n === 2740);
            const utgAccs = vatMonthly.filter(a => a.n >= 2700 && a.n <= 2709);
            const innAccs = vatMonthly.filter(a => a.n >= 2710 && a.n <= 2719);
            const fmt = Helpers.formatMoney;
            const issues = [];
            const terms = [];
            for (let ti = 0; ti < VAT_MI.length; ti++) {
                const mi = VAT_MI[ti];
                const m2 = MN[mi], m1 = MN[mi - 1], nextMonth = MN[mi + 1];
                if (!available.includes(m2)) continue;
                if (explicitPeriod && !available.includes(m1)) continue;
                const bal2740 = acc2740?.monthlyBalance?.[m2] ?? 0;
                let melding = null, utgSum = 0, innSum = 0;
                if (hasChange) {
                    const ch = (acc, mo) => acc?.monthlyChange?.[mo] ?? 0;
                    utgSum = utgAccs.reduce((s, a) => s + ch(a, m1) + ch(a, m2), 0);
                    innSum = innAccs.reduce((s, a) => s + ch(a, m1) + ch(a, m2), 0);
                    melding = utgSum + innSum;
                }
                const nonZero = [];
                const carried = [];
                // Previous term's end month (for carry-forward detection)
                const prevTermEnd = ti > 0 ? MN[VAT_MI[ti - 1]] : null;
                for (const acc of vatMonthly) {
                    if (acc.n === 2740 || acc.n < 2700 || acc.n > 2749) continue;
                    const b = acc.monthlyBalance[m2];
                    if (b === undefined || Math.abs(b) <= 1) continue;
                    const previousComparableBalance = prevTermEnd
                        ? (acc.monthlyBalance[prevTermEnd] ?? acc.openingBalance ?? 0) : 0;
                    // Compare balance with previous term — if unchanged, it's carry-forward
                    if (prevTermEnd) {
                        if (Math.abs(b - previousComparableBalance) < 1) {
                            carried.push({ account: acc.n, name: acc.name, balance: b, month: m2 });
                            continue;
                        }
                    }
                    // Periodisering across VAT-term boundaries can leave a temporary MVA balance
                    // at term end and reverse it in the following month. Do not flag that as open MVA.
                    if (nextMonth && available.includes(nextMonth)) {
                        const nextBalance = acc.monthlyBalance[nextMonth];
                        if (nextBalance !== undefined && Math.abs(nextBalance - previousComparableBalance) < 1) continue;
                    }
                    nonZero.push({ account: acc.n, name: acc.name, balance: b, month: m2 });
                }
                const source = config.vatSubmission;
                const period = resolvePeriod(config, transactions);
                const validSource = source?.source === 'Oppgaver' && source.year === period?.year
                    && /^\d{9}$/.test(String(config.orgNr || '')) && source.orgNr === config.orgNr;
                const submissionTerm = validSource ? source.terms?.find(row => row.term === ti + 1 && row.year === period.year) : null;
                const submissionStatus = submissionTerm?.status || 'unknown';
                const controlAccounts = vatMonthly.filter(a => a.n >= 2700 && a.n <= 2749 && a.n !== 2740);
                const balancesComplete = controlAccounts.every(a => Number.isFinite(a.monthlyBalance?.[m2]));
                const accountBalances = controlAccounts.filter(a => Number.isFinite(a.monthlyBalance?.[m2]) && Math.abs(a.monthlyBalance[m2]) > 1)
                    .map(a => ({account:a.n, name:a.name, balance:a.monthlyBalance[m2]}));
                terms.push({ term: ti + 1, label: TL[ti], m2, bal2740, melding, utgSum, innSum, nonZero, carried,
                    submissionStatus, submissionRawStatus: typeof submissionTerm?.rawStatus === 'string' ? submissionTerm.rawStatus : null,
                    submissionReason: submissionTerm?.reason || (validSource ? source.reason : source ? 'identity_unverified' : 'source_unavailable') || null,
                    ledgerCleared: controlAccounts.length && balancesComplete ? accountBalances.length === 0 : null,
                    accountBalances, declarationCoverage: 'unverified' });
                if (nonZero.length > 0) issues.push(terms[terms.length - 1]);
            }
            if (terms.length === 0) return partialTerm ? {
                type: 'info', title: t('rule_mva_period'), desc: t('mva_partial_period'),
                specialType: 'mva_partial_period', details: [],
            } : null;
            const partialNote = partialTerm ? '\n\n' + t('mva_partial_period') : '';
            if (issues.length === 0 && !terms.some(td => td.carried.length)) {
                return { type: 'info', title: t('rule_mva_period'), desc: t('rule_mva_period_ok') + `\n\n${t('mva_alle_ok', terms.length)}` + partialNote, details: [], evidence: { terms } };
            }
            // Present each account once; the original term calculations remain in evidence.
            const accountNumbers = new Set(terms.flatMap(td => [...td.nonZero, ...td.carried].map(a => a.account)));
            const latestMonth = MN.filter(month => available.includes(month)).at(-1);
            const monthLabel = month => t('month_short_' + (MN.indexOf(month) + 1));
            const currentBalances = vatMonthly.filter(a => accountNumbers.has(a.n)).map(a => {
                const balance = a.monthlyBalance[latestMonth];
                let since = latestMonth;
                const termMonths = terms.map(td => td.m2);
                const lastTermIndex = termMonths.indexOf(latestMonth);
                if (Number.isFinite(balance) && lastTermIndex >= 0) {
                    for (let i = lastTermIndex - 1; i >= 0; i--) {
                        if (MN.indexOf(termMonths[i + 1]) - MN.indexOf(termMonths[i]) !== 2
                            || a.monthlyBalance[termMonths[i]] !== balance) break;
                        since = termMonths[i];
                    }
                }
                const interval = since === latestMonth
                    ? t('mva_balance_latest', monthLabel(latestMonth))
                    : t('mva_balance_since', monthLabel(since), monthLabel(latestMonth));
                const name = a.name + ' (' + interval + ')';
                return { accNum: a.n, accName: name, name, ub: balance };
            });
            const onlyYearEndT6 = issues.every(td => String(td.label || '').startsWith('6 '));
            return {
                type: issues.length === 0 ? 'info' : (!onlyYearEndT6 && issues.length >= 3 ? 'error' : 'warning'),
                title: t('rule_mva_period'),
                desc: t('rule_mva_period_desc', monthLabel(latestMonth)) + partialNote,
                details: currentBalances,
                evidence: { newIssueTerms: issues.length, carriedTerms: terms.filter(td => td.carried.length).length, latestMonth, terms },
            };
        }
    },
    {
        id: 'private_car_7000',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc7000 = saldo.find(a => a.accNum === 7000 && Math.abs(a.ub) > 0);
            const acc7099 = saldo.find(a => a.accNum === 7099);
            return acc7000 && (!acc7099 || acc7099.ub === 0) ? { type: 'info', title: t('rule_bil'), desc: t('rule_bil_desc'), details: [acc7000] } : null;
        }
    },

    // --- RESULTATRAPPORT ---
    {
        id: 'wrong_sign_costs',
        category: 'result',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions = []) => {
            // Konta korekcyjne z natury (odwrotny znak jest normalny):
            // Feedback 2026-02-27 Patrycja: 7839 avsetning tap
            // Feedback 2026-03-23 Marzena: 6491 rabatter, 5945 OTP
            // Feedback 2026-03-25 batch (AK Kvalitet, Lukas Betong, TM Service, DK INOX, Anbynor, Jk Maling, Alfa Omega, Sawi Bygg):
            //   7790 (gebyr/2 bankkonti), 7041 (refusjon forsikring), 7045 (kreditnota), 7500 (kreditnota/odszkodowanie),
            //   7770 (przeksięgowanie), 7798 (przeksięgowanie), 5990 (refusjon), 5920 (kreditnota),
            //   6300 (korekta husleie), 6399 (korekta), 6725 (poprawne), 6800 (poprawne)
            const corrections = [
                4302, 4370,                     // varekjøp-korreksjon
                5090, 5091, 5092, 5093,         // feriepenger/Finagolønn periodisering (can reverse)
                5190, 5290,                     // lønn-korreksjon
                5920,                           // yrkesskadeforsikring (kreditnota)
                5945, 5950, 5951, 5990, 5999,   // pensjon/forsikring/refusjon
                6301, 6399, 6491,               // husleie-korreksjon, rabatter
                6725, 6800,                     // korreksjon kontor
                7041, 7045,                     // forsikring refusjon, kreditnota
                7500,                           // erstatning/kreditnota
                7740, 7770, 7790, 7798, 7839,   // diverse korreksjon/przeksięgowanie
            ];
            const found = [];
            // Zmiana rezerw (7830/7831) ujemna wyłącznie w pierwszym miesiącu okresu to rozwiązanie
            // rezerwy z 31.12 roku poprzedniego (Knu 11.10.2026: 7831 −1 670 871 w styczniu =
            // 732 427 z 1580 + 938 444 z 1530). Nie flagujemy, ale piszemy to na karcie.
            const provisionNotes = [];
            resultat.filter(r => r.accNum >= 4000 && r.accNum <= 7999 && !corrections.includes(r.accNum)
                && !(r.accNum >= 5800 && r.accNum <= 5899
                    && /\brefusjon\s+(?:av\s+)?(?:sykepenger|foreldrepenger)\b/i.test(String(r.accName || r.name || ''))))
            .forEach(acc => {
                // Jeśli brak danych miesięcznych — sprawdź sumę roczną
                if (!acc.months) {
                    if (Math.abs(acc.sum) < 500) return; // Próg 500 NOK (było 100) — feedback: -1,70 kr flagowane
                    const isWrongSign = config.costsArePositive ? acc.sum < 0 : acc.sum > 0;
                    if (isWrongSign) found.push(acc);
                    return;
                }
                const entries = Object.entries(acc.months);
                const flaggedMonths = [];
                entries.forEach(([month, val], idx) => {
                    if (Math.abs(val) < 500) return; // Próg 500 NOK (było 100)
                    const isWrongSign = config.costsArePositive ? val < 0 : val > 0;
                    if (!isWrongSign) return;
                    // Credit nota check: szukaj pasującej kwoty z odwrotnym znakiem w ±2 miesiącach (było ±1)
                    // Wzorzec z BANKAVST: tolerance matching z szerszym oknem
                    const absVal = Math.abs(val);
                    let isCreditNote = false;
                    for (let j = Math.max(0, idx - 2); j <= Math.min(entries.length - 1, idx + 2); j++) {
                        if (j === idx) continue;
                        const adjVal = entries[j][1];
                        const adjCorrectSign = config.costsArePositive ? adjVal > 0 : adjVal < 0;
                        if (adjCorrectSign && Math.abs(adjVal) >= 100) {
                            const adjAbs = Math.abs(adjVal);
                            // Tolerance 10% (było 5%) — wzorzec BANKAVST: łagodniejsze dopasowanie
                            if (Math.abs(adjAbs - absVal) / Math.max(adjAbs, absVal) < 0.10) {
                                isCreditNote = true;
                                break;
                            }
                        }
                    }
                    // Dodatkowy check: jeśli suma roczna ma PRAWIDŁOWY znak, to "wrong sign" w jednym miesiącu = korekta
                    if (!isCreditNote && acc.sum !== undefined) {
                        const yearlyCorrectSign = config.costsArePositive ? acc.sum > 0 : acc.sum < 0;
                        if (yearlyCorrectSign && Math.abs(val) < Math.abs(acc.sum) * 0.5) {
                            isCreditNote = true; // Miesiąc "odwrotny" ale roczna suma prawidłowa = korekta/refusjon
                        }
                    }
                    if (!isCreditNote) flaggedMonths.push(month);
                });
                if (flaggedMonths.length > 0) {
                    // Wyjątek tylko dla stycznia (przegląd 11.10.2026): okres maj–sierpień ma pierwszy
                    // miesiąc maj, a rozwiązanie rezerwy z 31.12 księguje się w styczniu.
                    const firstMonth = entries[0]?.[0];
                    const isJanuary = (m) => /^(0?1|jan)(?![0-9a-z])/i.test(String(m).trim());
                    const periodFromJan = !resolvePeriod(config) || resolvePeriod(config).monthFrom === 1;
                    if ([7830, 7831].includes(acc.accNum) && periodFromJan && isJanuary(firstMonth) && flaggedMonths.every(m => m === firstMonth)) {
                        provisionNotes.push(t('wrong_sign_provision_first_month', acc.id || acc.accNum, monthName(firstMonth)));
                        return;
                    }
                    found.push({ ...acc, anomalousMonths: flaggedMonths });
                }
            });
            // Explain observable cost-to-cost transfers without suppressing a finding or certifying treatment.
            const transferPeriod = resolvePeriod(config);
            const transferGroups = new Map();
            for (const tx of transactions || []) {
                const key = voucherKey(tx);
                if (!key || !transferPeriod) continue;
                if (!transferGroups.has(key)) transferGroups.set(key, []);
                transferGroups.get(key).push(tx);
            }
            const transferNotes = [];
            for (const acc of found) {
                const matches = [];
                for (const [key, lines] of transferGroups) {
                    if (lines.length < 2 || lines.some(tx => !Number.isInteger(tx.accountNo) || tx.accountNo < 4000 || tx.accountNo > 7999 || !Number.isFinite(tx.amount))
                        || new Set(lines.map(tx => tx.accountNo)).size !== 2
                        || new Set(lines.map(tx => String(tx.date || '').slice(0,7))).size !== 1
                        || Math.abs(lines.reduce((sum,tx) => sum + tx.amount,0)) > 0.01) continue;
                    const date = String(lines[0].date || '');
                    if (lines.some(tx => {
                        const d = String(tx.date || '');
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return true;
                        const parsed = new Date(d + 'T00:00:00Z');
                        const month = Number(d.slice(5,7));
                        return !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0,10) !== d
                            || Number(d.slice(0,4)) !== transferPeriod.year || month < transferPeriod.monthFrom || month > transferPeriod.monthTo;
                    })) continue;
                    const month = MONTH_SHORT[Number(date.slice(5,7))-1];
                    if (acc.anomalousMonths && !acc.anomalousMonths.some(m => Helpers.monthLabel(m) === month)) continue;
                    const amount = lines.filter(tx => tx.accountNo === acc.accNum).reduce((sum,tx) => sum + tx.amount,0);
                    if (config.costsArePositive ? amount >= -0.01 : amount <= 0.01) continue;
                    matches.push({key, ref: `${lines[0].transactionNo || lines[0].stampNo || lines[0].bundleId} (${isoToDotDate(date)})`, amount:Math.abs(amount)});
                }
                if (matches.length) transferNotes.push(t('wrong_sign_transfer_note', acc.accNum,
                    matches.map(m=>m.ref).join(', '), Helpers.formatMoney(matches.reduce((sum,m)=>sum+m.amount,0))));
            }
            // Severity: warning (było error) — feedback: zbyt wiele FP, pracownicy tracą czas na weryfikację poprawnych kont
            return found.length > 0 ? {
                type: 'warning', title: t('rule_wrong_sign_costs'),
                desc: t('rule_wrong_sign_costs_desc', config.name, config.costsArePositive ? t('positive') : t('negative')),
                details: found, source: 'resultat',
                why: t('wrong_sign_why'), verified: [...provisionNotes, ...transferNotes].length ? [...provisionNotes, ...transferNotes] : null, confidence: 'open',
            } : null;
        }
    },
    {
        id: 'anomalies_6xxx',
        category: 'analysis',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat) => {
            // Excluded: known sporadic costs (revision, consulting, leasing, insurance)
            // 6700-6703: Revisjonshonorar (audit fees — 1-2x/year)
            // 6720-6729: Rådgivning regnskap/juridisk (consulting — sporadic)
            const excluded = [6340, 6350, 6360, 6510, 6540, 6550, 6570, 6700, 6701, 6702, 6703, 6720, 6721, 6722, 6723, 6724, 6725, 6726, 6727, 6728, 6729, 6790, 6810, 6860];
            const isSporadic = (acc) =>
                excluded.includes(acc) ||
                (acc >= 6600 && acc <= 6699); // reparasjon og vedlikehold — sporadyczne z natury
            const costs = resultat.filter(r => r.accNum >= 6000 && r.accNum <= 6999 && !isSporadic(r.accNum));
            const anomalies = [];
            costs.forEach(acc => {
                const entries = Object.entries(acc.months);
                const values = entries.map(([, v]) => Math.abs(v));
                const active = values.filter(v => v > 100);
                if (active.length < 3) return;
                const avg = active.reduce((a, b) => a + b, 0) / active.length;
                // EN3 fix (2026-04-25): tiered spike threshold — proportional do skali konta.
                // Stara logika `> 5000 NOK` floor blokowała wykrywanie spike'ów na małych kontach
                // (np. avg 200 NOK, jeden m-c 2000 NOK = 10x average — ignored bo < 5000).
                // Nowa: floor proporcjonalny (max 5000 lub avg×3) — duże skoki na małych kontach łapane.
                const spikeFloor = Math.min(5000, Math.max(200, avg * 3));
                // Find first and last active month index
                const firstActive = values.findIndex(v => v > 100);
                const lastActive = values.length - 1 - [...values].reverse().findIndex(v => v > 100);
                // Group 64xx (leasing/inventar) — intermittent by nature, skip gap detection
                const is64xx = acc.accNum >= 6400 && acc.accNum <= 6499;
                const bad = [];
                entries.forEach(([m, val], idx) => {
                    const abs = Math.abs(val);
                    // Only flag gaps BETWEEN first and last active month (not trailing zeros = cost stopped)
                    if (abs === 0 && avg > 1000 && idx >= firstActive && idx <= lastActive && !is64xx) bad.push(m);
                    // Spike detection (>3.5x average AND > tiered floor) — skip 64xx (feedback 2026-02-27: za dużo FP)
                    else if (abs > avg * 3.5 && abs > spikeFloor && !is64xx) bad.push(m);
                    // Unusually low (>0 but <10% of avg) — applies to all except 64xx
                    else if (abs > 0 && abs < avg * 0.10 && !is64xx) bad.push(m);
                });
                if (bad.length > 0) anomalies.push({ ...acc, anomalousMonths: bad });
            });
            return anomalies.length > 0 ? { type: 'info', confidence: 'info', title: t('rule_anomalies_6xxx'), desc: t('rule_anomalies_6xxx_desc'), details: anomalies, source: 'resultat' } : null;
        }
    },
    {
        id: 'high_6540',
        category: 'analysis',
        systems: 'all',
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!transactions || transactions.length === 0) return null;
            const high = transactions
                .filter(tx => Number(tx.accountNo) === 6540 && Math.abs(Number(tx.amount) || 0) > 30000)
                .map(tx => ({
                    id: `${tx.stampNo || tx.bundleId || tx.linkId || tx.date || 'tx'}-${tx.amount}`,
                    accNum: 6540, voucherId: voucherKey(tx), stampNo: tx.stampNo, transactionNo: tx.transactionNo, date: tx.date, invoiceNo: tx.invoiceNo, description: tx.description,
                    name: tx.description || t('rule_high_6540'),
                    accName: [
                        tx.date,
                        tx.transactionNo ? `bilag ${tx.transactionNo}` : null,
                        tx.stampNo ? `stempel ${tx.stampNo}` : null,
                        tx.invoiceNo ? `faktura ${tx.invoiceNo}` : null,
                        tx.description,
                    ].filter(Boolean).join(' · '),
                    ib: 0,
                    change: Number(tx.amount) || 0,
                    ub: Number(tx.amount) || 0,
                }));
            return high.length > 0 ? { type: 'warning', title: t('rule_high_6540'), desc: t('rule_high_6540_desc'), details: high, source: 'transactions' } : null;
        }
    },
    {
        id: 'salary_sign',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const personal = resultat.filter(r => r.accNum >= 5000 && r.accNum <= 5999);
            if (personal.length === 0) return null;
            if (personal.some(row => !row.months || typeof row.months !== 'object' || Array.isArray(row.months)
                || (Object.keys(row.months).length === 0 && row.sum !== 0))) return notChecked('rule_salary_sign');
            const monthKeys = [...new Set(personal.flatMap(row => Object.keys(row.months || {})))].sort((a, b) => {
                const rank = key => MONTH_SHORT.includes(key) ? MONTH_SHORT.indexOf(key) : MONTH_SHORT.length;
                return rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0);
            });
            if (monthKeys.length === 0) return notChecked('rule_salary_sign');
            if (personal.some(row => Object.values(row.months || {}).some(value => !Number.isFinite(value)))) return notChecked('rule_salary_sign');
            const sumRow = { id: 'sum:5xxx', accNum: '5xxx', name: t('sumAccounts5xxx'), months: {}, anomalousMonths: [], sum: 0 };
            let hasWrongSign = false;
            let hasInvalidMonthlySum = false;
            monthKeys.forEach(month => {
                let s = 0;
                personal.forEach(row => { const value = row.months?.[month]; s += value === undefined ? 0 : value; });
                if (!Number.isFinite(s)) { hasInvalidMonthlySum = true; return; }
                sumRow.months[month] = s;
                if (Math.abs(s) > 10) {
                    if ((config.costsArePositive && s < 0) || (!config.costsArePositive && s > 0)) { sumRow.anomalousMonths.push(month); hasWrongSign = true; }
                }
            });
            if (hasInvalidMonthlySum) return notChecked('rule_salary_sign');
            sumRow.sum = Object.values(sumRow.months).reduce((a, b) => a + b, 0);
            if (!Number.isFinite(sumRow.sum)) return notChecked('rule_salary_sign');
            if (!hasWrongSign) return null;
            const contributingAccounts = personal
                .filter(row => sumRow.anomalousMonths.some(month => row.months?.[month] !== undefined && row.months[month] !== 0))
                .map(row => ({ ...row, months: Object.fromEntries(monthKeys.map(month => [month, row.months[month] ?? 0])) }));
            return {
                type: 'info', title: t('rule_salary_sign'), desc: t('rule_salary_sign_desc'),
                details: [...contributingAccounts, sumRow], source: 'resultat', specialType: 'review_signal',
            };
        }
    },
    {
        id: 'missing_5920',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const acc5000 = wageAccount(resultat, config);
            const isYrkesskade = (r) => {
                const name = String(r.accName || r.name || '').toLowerCase();
                return r.accNum === 5920 || name.includes('yrkesskade') || name.includes('arbeidsskade');
            };
            const acc5920 = resultat.find(r => isYrkesskade(r));
            if (!acc5000 || (acc5920 && Math.abs(acc5920.sum) > 0)) return null;
            const missing = acc5920 || { accNum: 5920, name: 'Yrkesskadeforsikring', sum: 0, months: {} };
            return { type: 'warning', title: t('rule_missing_5920'), desc: t('rule_missing_5920_desc'), details: [acc5000, missing], source: 'resultat' };
        }
    },
    {
        id: 'skattetrekk',
        category: 'compliance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat, config, _previousYear, _vatMonthly, transactions = []) => {
            const acc5000 = wageAccount(resultat, config);
            if (!acc5000) return null;
            // Skattetrekk: 2600-2699 (2600 Forskuddstrekk, 2690 Annet skattetrekk, etc.)
            const has26xxTransactions = Array.isArray(transactions) && transactions.some(tx =>
                Number.isInteger(tx.accountNo) && tx.accountNo >= 2600 && tx.accountNo <= 2699 && Number.isFinite(tx.amount) && tx.amount !== 0);
            const has26xxSummaryActivity = [...resultat, ...saldo].some(account => {
                if (!Number.isInteger(account.accNum) || account.accNum < 2600 || account.accNum > 2699) return false;
                return [account.sum, account.ib, account.change, account.ub, ...Object.values(account.months || {})]
                    .some(value => Number.isFinite(value) && value !== 0);
            });
            if (has26xxTransactions || has26xxSummaryActivity) return null;
            return { type: 'warning', title: t('rule_skattetrekk'), desc: t('rule_skattetrekk_desc'), details: [acc5000, { accNum: 2600, name: 'Forskuddstrekk', sum: 0, months: {} }], source: 'resultat' };
        }
    },
    {
        id: 'aga_5400',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const acc5000 = wageAccount(resultat, config);
            if (!acc5000) return null;
            const acc5400 = resultat.find(r => r.accNum === 5400);
            if (acc5400 && Math.abs(acc5400.sum) > 0) return null;
            const missingAga = acc5400 || { accNum: 5400, name: 'Arbeidsgiveravgift', sum: 0, months: {} };
            return { type: 'error', title: t('rule_aga'), desc: t('rule_aga_desc'), details: [acc5000, missingAga], source: 'resultat' };
        }
    },
    {
        id: 'feriepenger',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const acc5000 = wageAccount(resultat, config);
            if (!acc5000) return null;
            const has = [5020, 5021, 5090, 5091, 5092, 5093, 5210, 5211].some(n => { const a = resultat.find(r => r.accNum === n); return a && Math.abs(a.sum) > 0; });
            if (has) return null;
            return { type: 'warning', title: t('rule_feriepenger'), desc: t('rule_feriepenger_desc'), details: [acc5000, { accNum: 5210, name: 'Feriepenger', sum: 0, months: {} }], source: 'resultat' };
        }
    },
    {
        id: 'otp_5930',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            let otpPeriod = config?.period || null;
            if ((!otpPeriod || otpPeriod.monthFrom <= 1) && (config?.source || config?.apiSourceProofRequired)) {
                const proof=config?.sourceEvidence;
                if(proof?.status!=='verified' || !config.sourceIdentity || proof.sourceIdentity!==config.sourceIdentity
                    || proof.source!==config.source || !config.companyName || proof.companyName!==config.companyName
                    || !otpPeriod || proof.period?.year!==otpPeriod.year || proof.period?.monthFrom!==otpPeriod.monthFrom
                    || proof.period?.monthTo!==otpPeriod.monthTo) return notChecked('rule_otp','otp_source_unverified');
            }

            if (otpPeriod?.monthFrom > 1) {
                const ytd = config?.otpYtd;
                if (ytd?.status !== 'available' || !config.sourceIdentity || ytd.sourceIdentity !== config.sourceIdentity
                    || !config.source || ytd.source !== config.source || ytd.companyName !== config.companyName
                    || !config.companyName || ytd.period?.year !== otpPeriod.year
                    || ytd.period?.monthFrom !== 1 || ytd.period?.monthTo !== otpPeriod.monthTo
                    || !Array.isArray(ytd.resultat)) return notChecked('rule_otp','otp_ytd_missing');
                resultat = ytd.resultat;
                otpPeriod = ytd.period;
            }
            const mixedPension = resultat.some(r => r.accNum >= 5900 && r.accNum <= 5999
                && /\bafp\b|avtalefestet/i.test(r.accName || r.name || '')
                && /\botp\b|tjenestepensjon/i.test(r.accName || r.name || ''));
            if (mixedPension) return notChecked('rule_otp','otp_basis_mixed');
            const sign = config?.costsArePositive === false ? -1 : 1;
            const wages = resultat.filter(r => r.accNum >= 5000 && r.accNum <= 5099
                && ![5020, 5021, 5090, 5091, 5092, 5093, 5096].includes(r.accNum));
            const pensions = resultat.filter(r => !/\bafp\b|avtalefestet/i.test(r.accName || r.name || '')
                && ([5930, 5940, 5941, 5942, 5945, 5950].includes(r.accNum)
                || (r.accNum >= 5900 && r.accNum <= 5999 && /\botp\b|pensjon/i.test(r.name || r.accName || ''))));
            if ([...wages, ...pensions].some(r => r.sum == null || (typeof r.sum !== 'number' && typeof r.sum !== 'string') || String(r.sum).trim() === '' || !Number.isFinite(Number(r.sum)))) return notChecked('rule_otp');
            const salary = wages.reduce((sum, r) => sum + sign * Number(r.sum), 0);
            if (salary < 1) return resultat.some(r => r.accNum >= 5000 && r.accNum <= 5899 && Number(r.sum) * sign > 0) ? notChecked('rule_otp') : null;
            const pension = pensions.reduce((sum, r) => sum + sign * Number(r.sum), 0);
            const ratioPct = pension / salary * 100;
            const ratioText = ratioPct < 2 && Number(ratioPct.toFixed(2)) >= 2 ? '<2.00' : ratioPct.toFixed(2);
            // A P&L ratio is a screening signal, not an employee-level OTP assessment.
            const referenceGap = Math.max(0, salary * 0.02 - pension);
            const material = ratioPct < 2; // No false OK for a small monetary shortfall.
            return {
                type: material ? 'info' : 'success', title: t('rule_otp'),
                desc: t('otp_ratio_desc', Helpers.formatMoney(salary), Helpers.formatMoney(pension), ratioText)
                    + ' ' + t(material ? 'otp_ratio_review' : 'otp_ratio_context')
                    + ' ' + (otpPeriod ? t('otp_period_scope', otpPeriod.year, otpPeriod.monthFrom, otpPeriod.monthTo) : t('otp_period_unknown')),
                details: [...wages, ...pensions], source: 'resultat',
                specialType: material ? 'review_signal' : 'screened_ok',
                evidence: { salary, pension, ratioPct, referenceGap, material, basis: 'ledger_screening', period: otpPeriod },
                // Kontrakt karty (Knu 11.10.2026, „OTP: nie wiem, co sprawdzić"): werdykt ilościowy
                // wobec minimum 2 % i konkretne „co sprawdzić" w action_otp_5930.
                why: t('otp_why'),
                verified: [ratioPct >= 2 ? t('otp_ratio_ok', ratioText) : t('otp_ratio_low', ratioText)],
                confidence: material ? 'open' : 'info',
            };
        }
    },
    {
        id: 'periodisering',
        category: 'analysis',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat) => {
            const issues = [];
            // Pomijaj konta sporadyczne z natury (te same co anomalies_6xxx)
            const sporadicPeriod = [6340, 6350, 6360, 6510, 6540, 6550, 6570, 6700, 6701, 6702, 6703, 6720, 6721, 6722, 6723, 6724, 6725, 6726, 6727, 6728, 6729, 6790, 6810, 6860];
            resultat.filter(r => r.accNum >= 6300 && r.accNum <= 6999 && !sporadicPeriod.includes(r.accNum) && !(r.accNum >= 6600 && r.accNum <= 6699)).forEach(acc => {
                const months = Object.entries(acc.months);
                if (months.length < 6) return;
                const last = months[months.length - 1];
                const other = months.slice(0, -1).filter(([, v]) => Math.abs(v) > 0);
                if (other.length < 3) return;
                const avg = other.reduce((s, [, v]) => s + Math.abs(v), 0) / other.length;
                if (avg > 500 && Math.abs(last[1]) > avg * 4) {
                    issues.push({ type: 'warning', title: t('rule_period', last[0], acc.id), desc: t('rule_period_desc', acc.id, acc.name, last[0], Helpers.formatMoney(last[1]), Helpers.formatMoney(avg)), details: [{ ...acc, anomalousMonths: [last[0]] }], source: 'resultat' });
                }
            });
            return issues.length > 0 ? issues : null;
        }
    },
    {
        id: 'kundefordringer_vs_omsetning',
        category: 'analysis',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat) => {
            const acc1500 = saldo.find(a => a.accNum === 1500);
            if (!acc1500 || acc1500.ub <= 0) return null;
            const omsetning = resultat.filter(r => r.accNum >= 3000 && r.accNum <= 3999).reduce((s, r) => s + r.sum, 0);
            if (Math.abs(omsetning) === 0) return null;
            const ratio = acc1500.ub / Math.abs(omsetning);
            // Skaluj próg do długości okresu — krótszy okres = wyższe należności vs obrót to normalne
            // (feedback Marzena 2026-03-23: 2-miesięczny audyt, należności 40% = w terminie, płatne następny miesiąc)
            const monthSet = resultat.length > 0
                ? new Set(resultat.flatMap(r => r.months ? Object.keys(r.months) : []))
                : new Set();
            const monthCount = monthSet.size > 0 ? monthSet.size : 12;
            const threshold = Math.min(0.25 * (12 / monthCount), 1.5); // max 150%, min 25% (full year)
            return ratio > threshold ? { type: 'warning', title: t('rule_kundefordringer'), evidence: { omsetning, ratio, threshold }, desc: t('rule_kundefordringer_desc', Helpers.formatMoney(acc1500.ub), Helpers.formatMoney(omsetning), (ratio * 100).toFixed(0)), details: [acc1500] } : null;
        }
    },
    {
        id: 'revisjonsplikt',
        category: 'analysis',
        systems: 'all',
        requires: 'metadata',
        check: (_saldo, _resultat, config) => {
            const orgNr = String(config?.orgNr || '').replace(/\s/g, '');
            const accounts = config?.brreg?.accounts;
            const figures = accounts?.lastFigures;
            if (accounts?.status !== 'ok' || accounts.orgNr !== orgNr || !figures
                || !Number.isInteger(accounts.lastYear) || accounts.lastYear < 1 || figures.year !== accounts.lastYear
                || !Number.isFinite(figures.driftsinntekter) || !Number.isFinite(figures.balansesum)) return null;
            const { year, driftsinntekter, balansesum, basis } = figures;
            if (driftsinntekter < 7000000 && balansesum < 27000000) return null;
            const auditor = config?.brreg?.auditor;
            const source = `https://data.brreg.no/enhetsregisteret/api/enheter/${orgNr}/roller`;
            const valid = /^\d{9}$/.test(orgNr) && config?.brreg?.orgNr === orgNr
                && auditor?.source === source && typeof auditor?.checkedAt === 'string'
                && /^\d{4}-\d{2}-\d{2}T/.test(auditor.checkedAt) && Number.isFinite(Date.parse(auditor.checkedAt))
                && Array.isArray(auditor.auditors);
            const noAuditor = valid && auditor.status === 'empty' && auditor.auditors.length === 0;
            if (!noAuditor) return null; // Rewizor zarejestrowany albo brak wiarygodnego dowodu ról.
            const date = new Date(auditor.checkedAt).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
            const context = t('revisor_empty', date);
            const boundary = t('revisor_boundary');
            // Obowiązek z roku X dotyczy sprawozdania X+1 i pozostaje widoczny
            // w każdym audycie, dopóki Brreg nie potwierdzi zarejestrowanego rewizora.
            return { type: 'warning', title: t('rule_revisjon'),
                desc: `${t('rule_revisjon_desc', year, Helpers.formatMoney(driftsinntekter), Helpers.formatMoney(balansesum), year + 1)}\n\n${context}\n${boundary}`,
                details: [], source: 'brreg', confidence: 'confirmed',
                evidence: { year, auditYear: year + 1, driftsinntekter, balansesum, basis, orgNr,
                    source: `https://data.brreg.no/regnskapsregisteret/regnskap/${orgNr}`,
                    auditorSource: source, auditorCheckedAt: auditor.checkedAt },
                verified: [context, boundary, t('revisor_source', source)] };
        }
    },
    {
        id: 'revisjon_fravalg_mulig',
        category: 'analysis',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config, previousYear) => {
            const period = resolvePeriod(config);
            // Raz w roku: sprawozdania za poprzedni rok są już w Brreg,
            // a do końca roku pozostaje czas na decyzję generalforsamling.
            if (period?.monthTo !== 9) return null;
            // Ten sam kontrakt tożsamości i dowodu Brreg co revisjonsplikt.
            const orgNr = String(config?.orgNr || '').replace(/\s/g, '');
            const auditor = config?.brreg?.auditor;
            const source = `https://data.brreg.no/enhetsregisteret/api/enheter/${orgNr}/roller`;
            const hasAuditor = /^\d{9}$/.test(orgNr) && config?.brreg?.orgNr === orgNr
                && auditor?.source === source && typeof auditor?.checkedAt === 'string'
                && /^\d{4}-\d{2}-\d{2}T/.test(auditor.checkedAt) && Number.isFinite(Date.parse(auditor.checkedAt))
                && auditor.status === 'ok' && Array.isArray(auditor.auditors) && auditor.auditors.length > 0
                && auditor.auditors.every(a => /^\d{9}$/.test(a?.orgNr) && typeof a?.name === 'string' && a.name.trim());
            if (!hasAuditor) return null;
            const year = previousYear?.year;
            if (!period || !Number.isInteger(year) || year !== period.year - 1 || previousYear.saldoEndMonth !== 12) return null;
            const rows = previousYear.resultatFullYear;
            const balance = previousYear.saldo;
            if (!Array.isArray(rows) || !rows.length || !Array.isArray(balance) || !balance.length) return null;
            // Rzadkie mapy miesięcy są dozwolone per konto. Bez pokrycia całego
            // roku w raporcie nie domyślamy się zer w brakujących miesiącach.
            const months = new Set(rows.flatMap(r => Object.keys(r.months || {})));
            if (!MONTH_SHORT.every(m => months.has(m))) return null;
            const drift = rows.filter(r => r.accNum >= 3000 && r.accNum <= 3999);
            const assets = balance.filter(r => r.accNum >= 1000 && r.accNum <= 1999);
            if (!assets.length || drift.some(r => !Number.isFinite(r.sum)) || assets.some(r => !Number.isFinite(r.ub))) return null;
            const driftsinntekter = drift.reduce((sum, r) => sum + Math.abs(r.sum), 0);
            const balansesum = assets.reduce((sum, r) => sum + r.ub, 0);
            if (!Number.isFinite(driftsinntekter) || !Number.isFinite(balansesum)
                || driftsinntekter >= 7000000 || balansesum >= 27000000) return null;
            return {
                type: 'info', title: t('rule_revisjon_fravalg_mulig'),
                desc: t('rule_revisjon_fravalg_mulig_desc', Helpers.formatMoney(driftsinntekter), Helpers.formatMoney(balansesum), year),
                details: [...drift, ...assets], source: 'previousYear', confidence: 'info',
                evidence: { year, driftsinntekter, balansesum, saldoEndMonth: 12, orgNr,
                    auditorSource: source, auditorCheckedAt: auditor.checkedAt },
            };
        }
    },
    // === NOWE REGUŁY v17 ===
    // skattetrekk_bank — WYŁĄCZONA od 2026 (konto skattetrekk nie istnieje w nowym systemie)
    // Zostawione jako komentarz na wypadek audytów danych historycznych (pre-2026)
    // {
    //     id: 'skattetrekk_bank',
    //     category: 'balance',
    //     systems: 'all',
    //     requires: 'saldo',
    //     check: (saldo) => {
    //         const acc1950 = saldo.find(a => a.accNum === 1950);
    //         const accs26xx = saldo.filter(a => a.accNum >= 2600 && a.accNum <= 2699);
    //         const gjeld26 = accs26xx.reduce((s, a) => s + Math.abs(a.ub), 0);
    //         if (!acc1950 || accs26xx.length === 0) return null;
    //         const bank = Math.abs(acc1950.ub);
    //         if (gjeld26 < 100) return null;
    //         const diff = Math.abs(bank - gjeld26);
    //         if (diff < 100) return null;
    //         if (bank >= gjeld26) return null;
    //         const pctDiff = diff / gjeld26;
    //         if (pctDiff > 0.1) return { type: 'warning', title: t('rule_skattetrekk_bank'), desc: t('rule_skattetrekk_bank_desc', Helpers.formatMoney(acc1950.ub), Helpers.formatMoney(-gjeld26), Helpers.formatMoney(diff)), details: [acc1950, ...accs26xx] };
    //         return null;
    //     }
    // },
    {
        id: 'likviditetsgrad',
        category: 'analysis',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            // Od 1400: konta 13xx to finansielle anleggsmidler (NS 4102), nie majątek obrotowy.
            // Ten sam błąd naprawiony w RAPPORTERZE (6873fef5).
            const omlopsmidler = saldo.filter(a => a.accNum >= 1400 && a.accNum <= 1999).reduce((s, a) => s + a.ub, 0);
            // Saldo ze znakiem: konto zobowiązań z saldem debetowym (np. nadpłacony MVA) zmniejsza
            // dług. Suma wartości bezwzględnych dodawała je do długu.
            const kortsiktigGjeld = -saldo.filter(a => a.accNum >= 2300 && a.accNum <= 2999).reduce((s, a) => s + a.ub, 0);
            if (kortsiktigGjeld < 100) return null;
            const ratio = omlopsmidler / kortsiktigGjeld;
            if (ratio < 1.0) return { type: 'info', title: t('rule_likviditet'), desc: t('rule_likviditet_desc', ratio.toFixed(2), Helpers.formatMoney(omlopsmidler), Helpers.formatMoney(kortsiktigGjeld)), specialType: 'health' };
            return null;
        }
    },
    {
        id: 'leverandorgjeld_ratio',
        category: 'analysis',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc2400 = saldo.find(a => a.accNum === 2400);
            if (!acc2400 || Math.abs(acc2400.ub) < 1000) return null;
            const varekjop = saldo.filter(a => a.accNum >= 4000 && a.accNum <= 4599).reduce((s, a) => s + Math.abs(a.ub), 0);
            if (varekjop < 1000) return null;
            const pctNum = Math.abs(acc2400.ub) / varekjop * 100;
            const pct = pctNum.toFixed(0);
            if (pctNum > 40) return { type: 'info', title: t('rule_levgjeld'), evidence: { varekjop, pctNum }, desc: t('rule_levgjeld_desc', Helpers.formatMoney(acc2400.ub), Helpers.formatMoney(varekjop), pct), details: [acc2400] };
            return null;
        }
    },
    {
        id: 'husleie_consistency',
        category: 'analysis',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat) => {
            const rentAccs = resultat.filter(r => r.accNum >= 6300 && r.accNum <= 6309 && r.months && Math.abs(r.sum) > 1000);
            const issues = [];
            rentAccs.forEach(acc => {
                const vals = Object.entries(acc.months).filter(([, v]) => Math.abs(v) > 0).map(([m, v]) => ({ m, v: Math.abs(v) }));
                if (vals.length < 3) return;
                const avg = vals.reduce((s, x) => s + x.v, 0) / vals.length;
                const outliers = vals.filter(x => Math.abs(x.v - avg) > avg * 0.5);
                if (outliers.length >= 2) {
                    const monthList = vals.map(x => `${Helpers.monthLabel(x.m)}: ${Helpers.formatMoney(x.v)}`).join('\n');
                    issues.push({ type: 'info', title: t('rule_husleie'), desc: t('rule_husleie_desc', acc.id, acc.name, monthList), details: [{ ...acc, anomalousMonths: outliers.map(o => o.m) }], source: 'resultat' });
                }
            });
            return issues.length > 0 ? issues : null;
        }
    },
    {
        id: 'negativ_bank',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            // Próg -5 jak w neg_assets, z którego konta bankowe zostały tu przeniesione.
            const negative = saldo.filter(a => isBankAccount(a.accNum) && a.ub < -5);
            if (negative.length === 0) return null;
            const total = negative.reduce((s, a) => s + a.ub, 0);
            // Kontrakt karty (Knu 11.10.2026: „czy to saldo na koniec sierpnia, czy bank jest
            // zaksięgowany?"): data w opisie i ostatnia pozycja bankowa, gdy są transakcje. Okres tylko
            // z config.period rozstrzygniętego przez runAudit (bramka kompletności), bez własnego fallbacku.
            const period = resolvePeriod(config);
            // Ostatnia pozycja osobno dla każdego ujemnego konta, bez orzekania o kompletności
            // (Codex P2, 11.10.2026): data wpisu nie zastępuje uzgodnienia z wyciągiem.
            const lastIsoOf = (accNum) => (transactions || [])
                .filter(tx => tx.accountNo === accNum && /^\d{4}-\d{2}-\d{2}/.test(String(tx.date || '')))
                .map(tx => String(tx.date).slice(0, 10)).sort().pop() || null;
            const verified = (transactions && transactions.length)
                ? negative.map(a => {
                    const last = lastIsoOf(a.accNum);
                    if (!last) return t('bank_last_entry_none', a.accNum);
                    return period ? t('bank_last_entry', a.accNum, isoToDotDate(last), periodEndDate(period)) : t('bank_last_entry_nodate', a.accNum, isoToDotDate(last));
                })
                : [t('bank_last_entry_unknown')];
            return {
                type: 'warning', title: t('rule_neg_bank'),
                desc: period ? t('rule_neg_bank_desc_at', Helpers.formatMoney(total), periodEndDate(period)) : t('rule_neg_bank_desc', Helpers.formatMoney(total)),
                details: negative,
                why: t('neg_bank_why'), verified, confidence: 'open',
            };
        }
    },
    {
        id: 'aksjonaerlan_skatt',
        category: 'compliance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const acc1370 = saldo.find(a => a.accNum === 1370);
            if (!acc1370 || acc1370.ub <= 50000) return null;
            // Check account name — if it's investments (aksjer/andeler), not a shareholder loan
            const name = (acc1370.accName || acc1370.name || '').toLowerCase();
            const investKeywords = ['investering', 'aksje', 'andel', 'verdipapir', 'fondsandel'];
            if (investKeywords.some(kw => name.includes(kw))) return null;
            return { type: 'warning', title: t('rule_aksjonaerlan'), desc: t('rule_aksjonaerlan_desc', Helpers.formatMoney(acc1370.ub)), details: [acc1370] };
        }
    },
    {
        id: 'utbytte_2800',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config) => {
            const accounts = saldo.filter(a => a.accNum === 2800 || a.accNum === 2801);
            const net = accounts.reduce((sum, a) => sum + Number(a.ub || 0), 0);
            if (Math.abs(net) < 100) return null;
            // Kontrakt karty (Knu 11.10.2026): kolumna ZMIANA mówi, czy dywidenda z 31.12 ruszyła.
            // Ruch liczony po każdym koncie z osobna: przeniesienie 2800→2801 ma zmianę netto zero,
            // ale nie jest „brakiem ruchu" (Codex P2, 11.10.2026).
            const change = accounts.reduce((sum, a) => sum + Number(a.change || 0), 0);
            const anyMovement = accounts.some(a => Math.abs(Number(a.change || 0)) >= 1);
            const period = resolvePeriod(config);
            const movementKnown = accounts.every(a => typeof a.change === 'number' && Number.isFinite(a.change));
            const verified = [!movementKnown ? t('utbytte_change_unknown') : !anyMovement
                ? (period ? t('utbytte_unpaid', periodEndDate(period)) : t('utbytte_unpaid_period'))
                : t('utbytte_moved', Helpers.formatMoney(change))];
            return {
                type: 'info', title: t('rule_utbytte_2800'),
                desc: period ? t('dividend_net_desc_at', Helpers.formatMoney(net), periodEndDate(period)) : t('dividend_net_desc', Helpers.formatMoney(net)),
                details: accounts, evidence: { net },
                why: t('utbytte_why'), verified, confidence: 'info',
            };
        }
    },
    {
        id: 'no_accountant',
        category: 'compliance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat) => {
            const acc6705s = saldo.find(a => a.accNum === 6705);
            const acc6705r = resultat.find(a => a.accNum === 6705);
            const acc = acc6705s || acc6705r;
            if (acc && Math.abs(acc.ub || acc.sum || 0) > 100) return null;
            // Show account row with months if exists (all zeros), or empty details
            const details = acc ? [acc] : [];
            return { type: 'info', title: t('rule_no_accountant'), desc: t('rule_no_accountant_desc'), details, source: 'resultat' };
        }
    },
    {
        id: 'lonn_vs_aga',
        category: 'compliance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo) => {
            const lonn = saldo.filter(a => [5000, 5020].includes(a.accNum)).reduce((s, a) => s + Math.abs(a.ub), 0);
            const aga = saldo.filter(a => a.accNum === 5400).reduce((s, a) => s + Math.abs(a.ub), 0);
            if (lonn < 10000) return null;
            const pctNum = aga / lonn * 100;
            const pct = pctNum.toFixed(1);
            if (pctNum < 0.5 || pctNum > 20) return { type: 'warning', title: t('rule_lonn_aga'), desc: t('rule_lonn_aga_desc', Helpers.formatMoney(lonn), Helpers.formatMoney(aga), pct) };
            return null;
        }
    },

    // === NOWE REGUŁY v18 ===

    // --- Leasing 6440-6449: REMOVED — leasing nie musi być miesięczny (feedback 2026-02-24) ---

    // --- Banklån 8150-8159: jeśli używane → musi istnieć konto 2200-2229 w saldo ---
    {
        id: 'banklan_8150',
        category: 'compliance',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat) => {
            // Szukaj konta 8150 w resultat lub saldo
            const acc8150res = resultat.find(r => r.accNum === 8150 && Math.abs(r.sum) > 0);
            const acc8150sal = saldo.find(a => a.accNum === 8150 && Math.abs(a.ub) > 0);
            if (!acc8150res && !acc8150sal) return null;
            // Sprawdź nazwę konta — stare firmy mogą mieć 8150 jako "Annen finanskostnad" (nie banklån)
            // Flaguj TYLKO jeśli nazwa sugeruje banklån/kredyt (feedback Patrycja 2026-02-27)
            const accName = ((acc8150res || acc8150sal).accName || (acc8150res || acc8150sal).name || '').toLowerCase();
            const isBankLoan = accName.includes('banklån') || accName.includes('banklan') || accName.includes('rentekostnad banklån') || accName.includes('rentekostnad banklan');
            // Jeśli nazwa dostępna i NIE dotyczy banklån — pomiń (stary kontoplan, inne przeznaczenie konta 8150)
            if (accName && accName.length > 0 && !isBankLoan) return null;
            // Sprawdź czy istnieje banklån / kredyt w bilansie.
            // Feedback Artur 2026-05-22: część firm ma kredyt poza 2200-2229.
            const loanNameRe = /(banklån|banklan|kreditt|kassekreditt|lån|lan|finansiering|gjeld til kredittinstitusjon)/i;
            const hasBanklan = saldo.some(a => {
                if (Math.abs(a.ub) === 0) return false;
                const name = `${a.accName || ''} ${a.name || ''}`;
                if (a.accNum >= 2200 && a.accNum <= 2299) return true;
                return a.accNum >= 2300 && a.accNum <= 2499 && loanNameRe.test(name);
            });
            if (hasBanklan) return null;
            const displayName = (acc8150res || acc8150sal).accName || (acc8150res || acc8150sal).name || t('rule_banklan_8150_fallback');
            return { type: 'info', title: t('rule_banklan_8150'), desc: t('rule_banklan_8150_desc', displayName), details: [acc8150res || acc8150sal] };
        }
    },

    // --- Personalkostnader 5xxx: nagły skok/spadek >50% vs poprzedni miesiąc ---
    {
        id: 'salary_periodisering',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat, config) => {
            const personal = resultat.filter(r => r.accNum >= 5000 && r.accNum <= 5999 && r.months);
            if (personal.length === 0) return null;
            const monthKeys = Object.keys(personal[0].months);
            if (monthKeys.length < 3) return null;
            // Oblicz NETTO sumę 5xxx per miesiąc (bez Math.abs — żeby kwoty zgadzały się z 24SO)
            const monthlySums = {};
            monthKeys.forEach(m => {
                let s = 0;
                personal.forEach(row => s += (row.months[m] || 0));
                monthlySums[m] = s;
            });
            // Porównaj każdy miesiąc z poprzednim — skok >50% = anomalia (porównujemy wartości bezwzględne)
            const anomalies = [];
            const jumps = [];
            const entries = Object.entries(monthlySums);
            for (let i = 1; i < entries.length; i++) {
                const [prevM, prevV] = entries[i - 1];
                const [curM, curV] = entries[i];
                const absPrev = Math.abs(prevV);
                const absCur = Math.abs(curV);
                if (absPrev < 1000 && absCur < 1000) continue;
                if (absPrev < 100) { if (absCur > 1000) { anomalies.push(curM); jumps.push(`${monthName(prevM)} ${Helpers.formatMoney(prevV)} → ${monthName(curM)} ${Helpers.formatMoney(curV)}`); } continue; }
                const ratio = absCur / absPrev;
                if (ratio > 1.5 || ratio < 0.5) {
                    anomalies.push(curM);
                    const pct = Math.round((ratio - 1) * 100);
                    jumps.push(`${monthName(prevM)} ${Helpers.formatMoney(prevV)} → ${monthName(curM)} ${Helpers.formatMoney(curV)} (${pct > 0 ? '+' : ''}${pct} %)`);
                }
            }
            if (anomalies.length === 0) return null;
            // Buduj detail z sumami per miesiąc
            const sumRow = { id: 'SUM 5xxx', accNum: '5xxx', name: t('sumAccounts5xxx'), accName: t('sumAccounts5xxx'), months: monthlySums, anomalousMonths: anomalies, sum: Object.values(monthlySums).reduce((a, b) => a + b, 0) };
            // Info, nie ostrzeżenie: pomiar na portfelu 27.09.2026 (205 firm, okres 1-8) dał 102 firmy.
            // Feriepenger, sykepenger i bonusy ruszają pensje o ponad 50% u połowy klientów.
            // Kontrakt karty (Knu 11.10.2026, „to, co do kontroli, powinno być lepiej opisane"):
            // opis nazywa miesiące i kwoty, nie tylko próg.
            return {
                type: 'info', title: t('rule_salary_periodisering'),
                desc: t('rule_salary_periodisering_desc_months', jumps.join('; ')),
                details: [sumRow], source: 'resultat',
                why: t('salary_why'), confidence: 'info',
            };
        }
    },

    // === FAZA 5: Nowe reguły z istniejących danych (v23) ===

    // --- A1: Martwe miesiące — firma aktywna ale 0 transakcji w środku okresu ---
    {
        id: 'dead_months',
        category: 'compliance',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat) => {
            // Sumuj obroty per miesiąc (wszystkie konta 3xxx-8xxx)
            const opAccounts = resultat.filter(r => r.accNum >= 3000 && r.accNum <= 8999 && r.months);
            if (opAccounts.length === 0) return null;
            const monthKeys = Object.keys(opAccounts[0].months);
            if (monthKeys.length < 3) return null;
            const monthlyTotals = {};
            monthKeys.forEach(m => {
                let total = 0;
                opAccounts.forEach(acc => { total += Math.abs(acc.months[m] || 0); });
                monthlyTotals[m] = total;
            });
            // Szukaj martwych miesięcy: miesiąc N = 0, ale N-1 > 0 i N+1 > 0
            const entries = Object.entries(monthlyTotals);
            const deadMonths = [];
            for (let i = 1; i < entries.length - 1; i++) {
                const [prevM, prevV] = entries[i - 1];
                const [curM, curV] = entries[i];
                const [nextM, nextV] = entries[i + 1];
                if (curV < 100 && prevV > 1000 && nextV > 1000) {
                    deadMonths.push(curM);
                }
            }
            if (deadMonths.length === 0) return null;
            const monthLabels = deadMonths.map(m => Helpers.monthLabel(m)).join(', ');
            return {
                type: deadMonths.length >= 2 ? 'error' : 'warning',
                title: t('rule_dead_months'),
                desc: t('rule_dead_months_desc', monthLabels, deadMonths.length),
                details: deadMonths.map(m => ({ accNum: m, name: Helpers.monthLabel(m), ub: monthlyTotals[m] })),
            };
        }
    },

    // --- A2: Rozszerzony wykrywacz skoków kosztów (4xxx-7xxx, nie tylko 6xxx) ---
    {
        id: 'cost_spike_extended',
        category: 'analysis',
        systems: 'all',
        requires: 'resultat',
        check: (saldo, resultat) => {
            // Analizuj konta 4xxx-5xxx i 7xxx (6xxx już pokrywa anomalies_6xxx)
            // Wyklucz konta sporadyczne z natury: reisekostnad, annen personalkostnad, annen driftskostnad
            const isSporadicCost = (acc) =>
                (acc >= 7000 && acc <= 7199) || // reisekostnad (fly, hotell, diett, bilkostnader)
                acc === 4500 ||                 // fremmedytelse/underentreprise — varies by project
                acc === 5990 ||                 // annen personalkostnad
                acc === 7791;                   // annen driftskostnad
            const costs = resultat.filter(r =>
                ((r.accNum >= 4000 && r.accNum <= 5999) || (r.accNum >= 7000 && r.accNum <= 7999))
                && r.months
                && !isSporadicCost(r.accNum)
            );
            const anomalies = [];
            costs.forEach(acc => {
                const entries = Object.entries(acc.months);
                const values = entries.map(([, v]) => Math.abs(v));
                const active = values.filter(v => v > 100);
                if (active.length < 3) return;
                const avg = active.reduce((a, b) => a + b, 0) / active.length;
                if (avg < 500) return; // ignoruj drobne konta
                const spikeMonths = [];
                entries.forEach(([m, val]) => {
                    const abs = Math.abs(val);
                    if (abs > avg * 3.5 && abs > 5000) spikeMonths.push(m);
                });
                if (spikeMonths.length > 0) anomalies.push({ ...acc, anomalousMonths: spikeMonths });
            });
            return anomalies.length > 0 ? {
                type: 'info', confidence: 'info',
                title: t('rule_cost_spike_ext'),
                desc: t('rule_cost_spike_ext_desc', anomalies.length),
                details: anomalies,
                source: 'resultat'
            } : null;
        }
    },


    // === FAZA 6: Reguły oparte na transakcjach (GetTransactions) ===

    // --- B1: Duplikaty księgowań — ta sama kwota + data + konto w różnych bilagach ---
    {
        id: 'duplicate_entries',
        category: 'compliance',
        systems: ['24seven'],
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!transactions || transactions.length === 0) return null;
            // Strict evidence: invoice + one stable party + complete balanced voucher.
            // Descriptions and equal fees never establish document identity.
            const voucherRows = new Map<string, TransactionRow[]>();
            for (const tx of transactions) {
                const key = voucherKey(tx);
                if (key) voucherRows.set(key, [...(voucherRows.get(key) || []), tx]);
            }
            const parties = new Map<string, string>();
            const signatures = new Map<string, string>();
            for (const [key, rows] of voucherRows) {
                const payableRows = rows.filter(r => r.accountNo === 2400 && Math.abs(r.amount) > 0.01);
                if (!payableRows.length || payableRows.some(r => !String(r.customerId ?? '').trim())) continue;
                const ids = new Set(rows.map(r => String(r.customerId ?? '').trim()).filter(Boolean));
                if (ids.size !== 1) continue;
                const party = [...ids][0];
                parties.set(key, party);
                const invoices = new Set(rows.map(r => String(r.invoiceNo ?? '').trim()).filter(Boolean));
                if (invoices.size !== 1 || rows.some(r => !Number.isFinite(r.amount))) continue;
                if (!payableRows.every(r => r.amount < 0)) continue;
                if (rows.some(r => r.accountNo >= 1900 && r.accountNo <= 1999 && Math.abs(r.amount) > 0.01)) continue;
                if (Math.abs(rows.reduce((sum,r) => sum + Math.round(r.amount * 100), 0)) > 1) continue;
                const fingerprint = rows.map(r => JSON.stringify([r.accountNo, r.date,
                    Math.round(r.amount * 100), String(r.projectId ?? '').trim()])).sort();
                signatures.set(key, JSON.stringify([party, [...invoices][0], fingerprint]));
            }
            const partyOf = tx => parties.get(voucherKey(tx)) || `unknown:${voucherKey(tx)}`;
            // Grupuj po (accountNo, date, |amount|) — tylko konta kosztowe z realną kwotą
            const groups = {};
            let invalidAmount = false;
            transactions.forEach(tx => {
                if (!tx.accountNo || !tx.date) return;
                if (tx.accountNo < 4000 || tx.accountNo > 7999) return;
                if (tx.accountNo >= 5000 && tx.accountNo <= 5999) return; // lønn
                if (tx.accountNo >= 6300 && tx.accountNo <= 6399) return; // husleie
                if (tx.accountNo >= 7000 && tx.accountNo <= 7199) return; // reisekostnad — częste powtórzenia (diety, noclegi)
                // Feedback 2026-03-25: 4350 (fremmedtjenester/underleverandør) — częste powtórzenia kwot od różnych dostawców
                // 3 firmy (Lukas Garden, Sawi Bygg, Jk Maling) miały FP na 4350
                if (tx.accountNo >= 4350 && tx.accountNo <= 4399) return;
                if (!Number.isFinite(tx.amount)) { invalidAmount = true; return; }
                if (Math.abs(tx.amount) < 100) return;
                const key = `${tx.accountNo}|${tx.date}|${Math.abs(tx.amount).toFixed(2)}`;
                if (!groups[key]) groups[key] = [];
                groups[key].push(tx);
            });
            if (invalidAmount) return notChecked('rule_duplicates');
            // Flaguj grupy >1 — podgrupuj po tożsamości (faktura/opis) żeby znaleźć PRAWDZIWE duplikaty
            const candidates = Object.values(groups).filter(txs => txs.length > 1).flat();
            const unavailable = missingVoucherFinding(candidates, 'rule_duplicates');
            if (unavailable) return unavailable;
            const duplicates = [];
            let groupIdx = 0;
            let identifiedGroups = 0;
            let selfCancelled = 0;
            let nettedInvoices = 0;
            const getBilagId = voucherKey;
            // A reversal line "korrigering av <TransactionNo>" with the opposite amount on the
            // same account closes that voucher's copy. Knu Bygg 10.10.2026: invoice 23822783 was
            // booked twice and already reversed on 01.05, yet the pair stayed flagged because the
            // reversal carries another date. Ledger evidence, no model.
            // Binding: account, TransactionNo, opposite amount AND the same invoice number; the
            // comment names no journal or year, so a reversal silences a copy only when exactly one
            // line in the whole ledger carries that key (two journals sharing a number, Instalsos
            // 15:50301 and 25:50301, stay flagged). No counter, no order dependence (Codex rounds
            // 1 and 2, 10.10.2026). A reversal that is itself reversed does not count. Pairs
            // identified by description are never silenced.
            const correctionNo = (tx) => String(tx.description || '').match(/korrigering av (\d+)/i)?.[1];
            const invoiceOf = (tx) => String(tx.invoiceNo ?? '').trim() ? `inv:${JSON.stringify([partyOf(tx), String(tx.invoiceNo).trim()])}` : null;
            const bindable = (tx) => Number(tx.transactionNo) > 0 && Number.isFinite(tx.amount) && !!invoiceOf(tx);
            const selfKey = (tx) => `${tx.accountNo}|${tx.transactionNo}|${tx.amount.toFixed(2)}|${invoiceOf(tx)}`;
            const targetKey = (tx) => `${tx.accountNo}|${correctionNo(tx)}|${(-tx.amount).toFixed(2)}|${invoiceOf(tx)}`;
            const occurrences = new Map<string, number>();
            for (const tx of transactions) if (bindable(tx)) occurrences.set(selfKey(tx), (occurrences.get(selfKey(tx)) || 0) + 1);
            // A reversal is void when any "korrigering av <its number>" line exists on the same
            // account, whatever its amount or invoice (Codex rounds 3 and 4: a cancellation may lack
            // the invoice number or be split into several lines). Conservative by design: this can
            // only keep a warning, never hide one.
            const allCorrections = transactions.filter(tx => Number.isFinite(tx.amount) && correctionNo(tx));
            // A line naming its own voucher (Knu 50206: booking and "korrigering av 50206" in one
            // voucher) reverses a sibling line, it does not undo a reversal.
            const undone = new Set(allCorrections.filter(c => String(c.transactionNo) !== correctionNo(c)).map(c => `${c.accountNo}|${correctionNo(c)}`));
            // Net check: the whole ledger must carry exactly one signed copy of account|invoice|amount,
            // otherwise a self-contained reversal pair could hide a surviving duplicate.
            const netKey = (tx) => `${tx.accountNo}|${Math.abs(tx.amount).toFixed(2)}|${invoiceOf(tx)}`;
            const net = new Map<string, number>();
            for (const tx of transactions) if (Number.isFinite(tx.amount) && invoiceOf(tx)) net.set(netKey(tx), (net.get(netKey(tx)) || 0) + tx.amount);
            const corrections = allCorrections.filter(invoiceOf);
            const silenced = new Set<string>();
            for (const c of corrections) {
                if (Number(c.transactionNo) > 0 && undone.has(`${c.accountNo}|${c.transactionNo}`)) continue;
                if (occurrences.get(targetKey(c)) !== 1) continue;
                if (Math.abs((net.get(netKey(c)) || 0) - (-c.amount)) > 0.011) continue;
                silenced.add(targetKey(c));
            }
            const isReversed = (tx) => bindable(tx) && silenced.has(selfKey(tx));
            // Storno without "korrigering" text (Knu 15.05.2026: invoice 132 booked as 51180,
            // reversed by 50694, rebooked as 50697 with a project). Plain lines of one invoice,
            // account and absolute amount are summed over the whole ledger; when at most one signed
            // copy survives, the pair is a storno + rebooking, not a duplicate. Korrigering lines
            // are excluded here because they have their own, stricter path above; description-
            // identified pairs are never netted (a refund may legitimately share amount and day).
            // Zwykłe storno (bez tekstu „korrigering"), po trzech rundach audytu Codexa 11–12.10.2026.
            // Ogólna arytmetyka salda faktury za każdym razem otwierała nowy układ ukrywający duplikat,
            // więc zasada jest zachowawcza i lokalna: zwykła linia przeciwnego znaku kasuje JEDNĄ kopię
            // faktury N, gdy (a) ma ten sam numer faktury albo jawne odwołanie do N w opisie, (b) ma
            // dokładnie kwotę pary, (c) sama nie została cofnięta linią „korrigering av <jej bilag>".
            // Korekty „korrigering" zostają wyłącznie w starszej ścieżce powyżej. Tożsamości po opisie
            // nie są nettowane. Rozbite noty (−600/−400) i łańcuchy korekt pozostawiają ostrzeżenie:
            // lepiej jedno pytanie do księgowej niż ukryty duplikat. Odwołanie w opisie: jawne słowo
            // dokumentu i pełny token numeru („til 31.12", „12 345,00", „12345Å", „2026-001" odpadają).
            // Bez lookbehind (Safari przed 16.4 na domyślnej liście Vite): granica jako konsumowany prefiks.
            const referencedInvoices = (tx) => {
                const d = String(tx.description || '');
                const out = new Set<string>();
                for (const m of d.matchAll(/(^|[^\p{L}\d])(?:fv|faktura|fakt|invoice|inv|kreditnota)\.?(?:\s+til)?\s*(?:nr\.?|no\.?|#)?\s*(\d{2,})(?![\p{L}\d]|[.,\-\/][\p{L}\d]|\s+\d{3}(?!\d))/giu)) out.add(m[2]);
                return [...out];
            };
            const plainStorno = (tx) => Number.isFinite(tx.amount) && !correctionNo(tx)
                && !(Number(tx.transactionNo) > 0 && undone.has(`${tx.accountNo}|${tx.transactionNo}`));
            // Budżet storn: konto | kwota | znak | faktura → linie, które mogą skasować kopię. Każda linia
            // jest zużywana najwyżej raz, także gdy wskazuje kilka faktur albo pasuje do kilku dat
            // (Codex runda 4); linie wyzerowane w bilagu wypadają z budżetu.
            const stornoLines = new Map<string, TransactionRow[]>();
            const usedStorno = new Set<TransactionRow>();
            const stornoKey = (acc, amount, sign, inv) => `${acc}|${Math.abs(amount).toFixed(2)}|${sign}|${inv}`;
            for (const tx of transactions) {
                if (!plainStorno(tx)) continue;
                const own = invoiceOf(tx);
                const targets = new Set(referencedInvoices(tx).map(n => `inv:${JSON.stringify([partyOf(tx), n])}`));
                if (own) targets.add(own);
                for (const inv of targets) {
                    const k = stornoKey(tx.accountNo, tx.amount, Math.sign(tx.amount), inv);
                    if (!stornoLines.has(k)) stornoLines.set(k, []);
                    stornoLines.get(k).push(tx);
                }
            }
            // k kopii o kwocie X: potrzeba k−1 niezużytych storn −X związanych z fakturą; zużyte znikają.
            const nettedToOneCopy = (copies) => {
                const first = copies[0];
                const perCopy = copies.filter(tx => voucherKey(tx) === voucherKey(first)).length;
                const need = copies.length - perCopy;
                const pool = (stornoLines.get(stornoKey(first.accountNo, first.amount, -Math.sign(first.amount), invoiceOf(first))) || []).filter(l => !usedStorno.has(l));
                if (pool.length < need) return false;
                for (const l of pool.slice(0, need)) usedStorno.add(l);
                return true;
            };
            // Tożsamość transakcji: numer faktury (najlepszy), albo opis (np. nazwisko pracownika).
            // EN2 fix (2026-04-25): normalize description przed match (strip non-alphanumeric, lowercase).
            // Wcześniej "VAT", "vat ", "VAT  " traktowane jako 3 różne identity → false positives.
            // Teraz: "vat" dla wszystkich (po lowercase + strip whitespace/punctuation).
            const normalizeDesc = (s: any): string => String(s || '')
                .toLowerCase()
                .replace(/[^a-z0-9æøåąćęłńóśźż]/g, '')
                .trim();
            const getIdentity = (tx) => invoiceOf(tx) || `desc:${normalizeDesc(tx.description)}`;

            for (const [key, txs0] of Object.entries(groups)) {
                if (txs0.length <= 1) continue;
                // A voucher whose lines of ONE document identity in this group sum to zero contributes
                // nothing (Knu 10.08.2026: voucher 1961 carries +450 and −450 on 6810, the live +450 sits
                // in 1962). Identity keeps A/+1000 and B/−1000 of a collective voucher apart (Codex P2);
                // only a line saying "korrigering av <its OWN voucher>" takes the identity of the sibling
                // it reverses (the Knu reversal carries that text while the booking has none). A line
                // correcting ANOTHER voucher never zeroes its siblings (Codex round 3).
                const perVoucher = new Map<string, number>();
                const selfNamed = (tx) => correctionNo(tx) && Number(tx.transactionNo) > 0 && correctionNo(tx) === String(tx.transactionNo);
                // Tożsamości zwykłych linii per bilag, policzone raz na grupę (Codex runda 4: skan w pętli był kwadratowy).
                const plainIdsByVoucher = new Map<string, Set<string>>();
                for (const l of txs0) {
                    if (correctionNo(l)) continue;
                    const v = voucherKey(l);
                    if (!v) continue;
                    if (!plainIdsByVoucher.has(v)) plainIdsByVoucher.set(v, new Set());
                    plainIdsByVoucher.get(v).add(getIdentity(l));
                }
                const zeroIdentity = (tx) => {
                    if (!correctionNo(tx)) return getIdentity(tx);
                    if (!selfNamed(tx)) return null; // korekta innego bilagu: nie bierze udziału w zerowaniu
                    const ids = plainIdsByVoucher.get(voucherKey(tx));
                    return ids && ids.size === 1 ? [...ids][0] : getIdentity(tx);
                };
                const keyOf = new Map<TransactionRow, string | null>();
                for (const tx of txs0) { const v = voucherKey(tx); const id = zeroIdentity(tx); keyOf.set(tx, v && id ? `${v}|${id}` : null); }
                for (const tx of txs0) { const k = keyOf.get(tx); if (k) perVoucher.set(k, (perVoucher.get(k) || 0) + tx.amount); }
                const zeroed = (tx) => { const k = keyOf.get(tx); return !!k && Math.abs(perVoucher.get(k)) <= 0.011; };
                const txs = txs0.filter(tx => !zeroed(tx));
                // Linia wyzerowana w bilagu nie może już kasować kopii w innym bilagu (Codex runda 4).
                for (const tx of txs0) if (zeroed(tx)) usedStorno.add(tx);
                for (const s of perVoucher.values()) if (Math.abs(s) <= 0.011) selfCancelled++;
                if (txs.length <= 1) continue;
                // Podgrupuj po tożsamości — różne faktury/opisy = różne transakcje
                const subGroups = {};
                txs.forEach(tx => {
                    const signature = signatures.get(voucherKey(tx));
                    if (!invoiceOf(tx) || !signature) return;
                    const id = `${getIdentity(tx)}|${signature}`;
                    if (!subGroups[id]) subGroups[id] = [];
                    subGroups[id].push(tx);
                });

                // W każdej podgrupie sprawdź czy są różne bilagi (stempel) — to prawdziwe duplikaty
                for (const [id, subTxs] of Object.entries(subGroups)) {
                    // Compare multiplicity within each sign and identity. An unrelated refund
                    // must not hide two positive copies, while one positive/negative pair is not a duplicate.
                    const signedGroups = [subTxs.filter(tx => tx.amount > 0), subTxs.filter(tx => tx.amount < 0)];
                    for (const signedAll of signedGroups) {
                        const signedTxs = signedAll.filter(tx => !isReversed(tx));
                        if (signedTxs.length <= 1) continue;
                        if (id.startsWith('inv:') && nettedToOneCopy(signedTxs)) { nettedInvoices++; continue; }
                        // Wspólny stempel kopii nie dowodzi związku z linią przeciwną (Codex runda 2); ścieżka
                        // „ten sam stempel + anonimowe storno" usunięta, przypadek Knu domyka odwołanie w opisie.
                        const uniqueBilags = new Set(signedTxs.map(getBilagId).filter(Boolean));
                        if (uniqueBilags.size <= 1) continue;
                        groupIdx++;
                        const evidenceStrength = 'identified';
                        identifiedGroups++;
                        for (const tx of signedTxs) {
                            const refs = [voucherKey(tx)];
                            if (tx.stampNo) refs.push(`${t('tx_stempel')} ${tx.stampNo}`);
                            if (tx.linkId) refs.push(`${t('tx_linkid')} ${tx.linkId}`);
                            if (tx.bundleId && !tx.linkId) refs.push(`${t('tx_bundleid')} ${tx.bundleId}`);
                            if (tx.invoiceNo) refs.push(`${t('tx_faktura')} ${tx.invoiceNo}`);
                            duplicates.push({
                                groupId: groupIdx, evidenceStrength,
                                voucherId: voucherKey(tx), date: tx.date, invoiceNo: tx.invoiceNo, description: tx.description,
                                accNum: tx.accountNo,
                                name: `#${groupIdx} | ${refs.join(' | ') || '?'} | ${tx.date} | ${Helpers.formatMoney(tx.amount)} | ${tx.description || '—'}`,
                                accName: tx.description || '',
                                ub: tx.amount,
                            });
                        }
                    }
                }
            }
            const verified = [(selfCancelled + nettedInvoices) > 0 ? t('duplicates_netted', selfCancelled, nettedInvoices) : t('duplicates_nothing_netted')];
            if (duplicates.length === 0 && signatures.size === 0) return notChecked('rule_duplicates');
            if (duplicates.length === 0) return {
                type: 'success', title: t('rule_duplicates'), desc: t('rule_duplicates_ok'),
                details: [], specialType: 'tx_passed', verified, confidence: 'info',
            };
            return {
                type: identifiedGroups ? 'warning' : 'info',
                specialType: identifiedGroups ? undefined : 'review_signal',
                title: t(identifiedGroups ? 'rule_duplicates' : 'duplicates_weak_title'),
                desc: t('duplicates_evidence_desc', groupIdx, identifiedGroups, groupIdx - identifiedGroups),
                details: duplicates,
                why: t('duplicates_why'), verified, confidence: 'open',
            };
        }
    },

    // 04.11.2026: odcisk pozycji ogranicza fałszywe alarmy przy różnych numerach faktur sprzedaży.
    {
        id: 'duplicate_sales_invoices',
        category: 'compliance',
        systems: ['24seven'],
        requires: 'transactions',
        check: (_saldo, _resultat, _config, _previousYear, _vatMonthly, transactions = []) => {
            const nonEmpty = value => String(value ?? '').trim();
            if (!transactions.some(tx => nonEmpty(tx.invoiceLinesKey))) {
                return notChecked('rule_duplicate_sales', 'invoice_lines_missing');
            }
            const vouchers = new Map<string, TransactionRow[]>();
            for (const tx of transactions) {
                const key = voucherKey(tx);
                if (!key) continue;
                if (!vouchers.has(key)) vouchers.set(key, []);
                vouchers.get(key).push(tx);
            }
            const groups = new Map();
            const credits: { customer: string; pair: string; fingerprints: Set<string> }[] = [];
            for (const [voucherId, rows] of vouchers) {
                if (rows.some(row => !Number.isFinite(row.amount))) continue;
                const balances = new Map<number, number>();
                for (const row of rows) {
                    balances.set(row.accountNo, (balances.get(row.accountNo) || 0) + Math.round(row.amount * 100));
                }
                const accounts = [...balances].filter(([, amount]) => amount !== 0).sort(([a], [b]) => a - b);
                if (accounts.reduce((sum, [, amount]) => sum + amount, 0) !== 0) continue;
                if (accounts.some(([account]) => account >= 1900 && account <= 1999)) continue;
                const customerRows = rows.filter(row => row.accountNo >= 1500 && row.accountNo <= 1599);
                const customers = new Set(customerRows.map(row => nonEmpty(row.customerId)).filter(Boolean));
                const invoices = new Set(rows.map(row => nonEmpty(row.invoiceNo)).filter(Boolean));
                if (customers.size !== 1 || invoices.size !== 1) continue;
                const customer = [...customers][0];
                const invoiceNo = [...invoices][0];
                const amount = accounts.filter(([account]) => account >= 1500 && account <= 1599)
                    .reduce((sum, [, balance]) => sum + balance, 0);
                const pair = JSON.stringify([customer, Math.abs(amount)]);
                const fingerprints = new Set(customerRows.map(row => nonEmpty(row.invoiceLinesKey)).filter(Boolean));
                if (amount < 0) {
                    credits.push({ customer, pair, fingerprints });
                    continue;
                }
                if (amount < 100000) continue;
                if (fingerprints.size !== 1) continue;
                const date = customerRows[0].date;
                const key = JSON.stringify([customer, date, amount, [...fingerprints][0], accounts]);
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push({ voucherId, invoiceNo, customer, pair, fingerprint: [...fingerprints][0], rows: customerRows });
            }
            const details = [];
            let groupId = 0;
            for (const copies of groups.values()) {
                if (new Set(copies.map(copy => copy.invoiceNo)).size < 2) continue;
                const matchingCredits = credits.filter(credit => credit.customer === copies[0].customer
                    && (credit.pair === copies[0].pair || credit.fingerprints.has(copies[0].fingerprint))).length;
                if (copies.length - matchingCredits < 2) continue;
                groupId++;
                for (const copy of copies) for (const tx of copy.rows) {
                    const refs = [copy.voucherId];
                    if (tx.stampNo) refs.push(`${t('tx_stempel')} ${tx.stampNo}`);
                    if (tx.linkId) refs.push(`${t('tx_linkid')} ${tx.linkId}`);
                    if (tx.bundleId && !tx.linkId) refs.push(`${t('tx_bundleid')} ${tx.bundleId}`);
                    refs.push(`${t('tx_faktura')} ${copy.invoiceNo}`);
                    details.push({
                        groupId, evidenceStrength: 'identified',
                        voucherId: copy.voucherId, date: tx.date, invoiceNo: copy.invoiceNo, description: tx.description,
                        accNum: tx.accountNo,
                        name: `#${groupId} | ${refs.join(' | ')} | ${tx.date} | ${Helpers.formatMoney(tx.amount)} | ${tx.description || '-'}`,
                        accName: tx.description || '', ub: tx.amount,
                    });
                }
            }
            return groupId ? {
                type: 'warning', title: t('rule_duplicate_sales'), desc: t('duplicate_sales_desc', groupId),
                details, why: t('duplicate_sales_why'), verified: [t('duplicate_sales_verified')], confidence: 'open',
            } : {
                type: 'success', title: t('rule_duplicate_sales'), desc: t('rule_duplicate_sales_ok'),
                details: [], specialType: 'tx_passed', confidence: 'info',
            };
        }
    },

    // --- B2: Brakujący MVA per bilag — voucher kosztowy bez wpisu MVA ---
    // GLOBAL-03: no source invoice tax evidence in period ledger postings.
    {
        id: 'mva_per_voucher', category: 'compliance', systems: ['24seven'], requires: 'transactions',
        check: (_saldo, _resultat, config) => isKnownNotMvaRegistered(config) ? null
            : notChecked('rule_mva_voucher', 'vat_document_evidence_missing'),
    },

    {
        id: 'mva_sales_voucher',
        category: 'compliance',
        systems: ['24seven'],
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (isKnownNotMvaRegistered(config)) return null;
            if (!transactions || transactions.length === 0) return null;
            const unavailable = missingVoucherFinding(transactions, 'rule_mva_sales');
            if (unavailable) return unavailable;
            const bundles = {};
            transactions.forEach(tx => {
                const bid = voucherKey(tx);
                if (!bid) return;
                if (!bundles[bid]) bundles[bid] = [];
                bundles[bid].push(tx);
            });
            // Konta niebędące zwykłą sprzedażą — nie wymagają utgående MVA
            const isNonSalesRevenue = (acc) =>
                acc === 3009 || acc === 3099 ||           // påløpt inntekt (periodisering)
                (acc >= 3800 && acc <= 3899);             // gevinst/tap ved salg av anleggsmidler (mot konto)

            const missing = [];
            let checked = 0;
            for (const [bid, txs] of Object.entries(bundles)) {
                // Tylko konto 3000 (główna sprzedaż z VAT) — inne 3xxx (3100 avgiftsfri, 3009 påløpt, 3900 annen) nie wymagają utgående MVA
                const salesTxs = txs.filter(tx => tx.accountNo === 3000);
                if (salesTxs.length === 0) continue;
                // Sprawdź czy jest utgående MVA (2700-2709)
                const hasUtgMVA = hasNetVatAccount(txs, 2700, 2709);

                // Periodisering detection: bilagi z kontami 17xx (forskuddsbetalt) lub 29xx (påløpte)
                const hasPeriodisering = txs.some(tx =>
                    (tx.accountNo >= 1700 && tx.accountNo <= 1799) ||
                    (tx.accountNo >= 2900 && tx.accountNo <= 2999)
                );
                if (hasPeriodisering) continue;
                // Periodisering / interne posteringer — skip
                const hasKundefordring = txs.some(tx => tx.accountNo >= 1500 && tx.accountNo <= 1599);
                const hasBank = txs.some(tx => tx.accountNo >= 1900 && tx.accountNo <= 1999);
                if (!hasKundefordring && !hasBank) continue; // intern — skip
                // Drobne beløp skip
                const salesSum = Math.abs(salesTxs.reduce((s, tx) => s + tx.amount, 0));
                if (salesSum < 500) continue;
                checked++;
                if (hasUtgMVA) continue;
                const firstSale = salesTxs[0];
                const salesAccounts = [...new Set(salesTxs.map(tx => tx.accountNo))].join(', ');
                const descriptions = [...new Set(salesTxs.map(tx => tx.description).filter(Boolean))].join('; ');
                const refs = [];
                const anyTx = txs[0];
                if (anyTx?.stampNo) refs.push(`${t('tx_stempel')} ${anyTx.stampNo}`);
                if (anyTx?.linkId) refs.push(`${t('tx_linkid')} ${anyTx.linkId}`);
                if (anyTx?.invoiceNo) refs.push(`${t('tx_faktura')} ${anyTx.invoiceNo}`);
                missing.push({
                    accNum: anyTx?.stampNo || bid,
                    voucherId: bid, date: firstSale?.date, accounts: salesAccounts, description: descriptions,
                    name: `${firstSale?.date || '?'} | ${refs.join(' | ') || `${t('tx_id')} ${bid}`} | ${t('tx_konto')} ${salesAccounts} | ${descriptions || '\u2014'}`,
                    accName: descriptions || '',
                    ub: salesSum,
                });
            }
            if (missing.length === 0) return checked === 0
                ? notChecked('rule_mva_sales', 'mva_scope_only')
                : { type: 'success', title: t('rule_mva_sales'), desc: t('mva_subset_passed', checked), details: [], specialType: 'tx_passed' };
            const shown = missing.slice(0, 20);
            return {
                type: 'info', specialType: 'review_signal', evidence: missing,
                title: t('rule_mva_sales'),
                desc: t('rule_mva_sales_desc', missing.length, missing.length > 20 ? ` (${t('showing')} 20)` : ''),
                details: shown,
            };
        }
    },

    // --- B5: Luki w numeracji faktur wychodzących (InvoiceNo) — brakujące numery w sekwencji ---
    // GLOBAL-02: InvoiceNo postings are not a complete sales invoice register.
    {
        id: 'invoice_numbering', category: 'compliance', systems: ['24seven'], requires: 'transactions',
        check: () => notChecked('rule_invoice_numbering', 'invoice_registry_missing'),
    },

    {
        id: 'project_pnl_summary',
        category: 'analysis',
        systems: ['24seven'],
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!transactions || transactions.length === 0) return null;
            // Self-detection: czy klient używa Project dim?
            const costTxs = transactions.filter(tx => tx.accountNo >= 4000 && tx.accountNo <= 7999);
            if (costTxs.length === 0) return null;
            const withProject = costTxs.filter(tx => tx.projectId).length;
            const projectUsageRatio = withProject / costTxs.length;
            if (projectUsageRatio < 0.20) return null; // klient nie używa projektów

            // Source ID identifies a project; names are labels and may be shared.
            const byProject: Record<string, { name: string; projectId: string | null; key: string; costs: number; sales: number; txCount: number; firstDate: string | null; lastDate: string | null }> = {};
            for (const tx of transactions) {
                if (!tx.projectName) continue;
                const projectId=tx.projectId ? String(tx.projectId) : null;
                const key = projectId ? 'id:'+projectId : 'name:'+tx.projectName;
                if (!byProject[key]) byProject[key] = { name: tx.projectName, projectId, key, costs: 0, sales: 0, txCount: 0, firstDate: null, lastDate: null };
                byProject[key].txCount++;
                // Kontrakt karty (Knu 11.10.2026): zakres dat projektu = pierwsza i ostatnia pozycja w okresie.
                const d = String(tx.date || '').slice(0, 10);
                if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
                    const p = byProject[key];
                    if (!p.firstDate || d < p.firstDate) p.firstDate = d;
                    if (!p.lastDate || d > p.lastDate) p.lastDate = d;
                }
                // Sprzedaz: 3xxx (24SO costsArePositive: sales = ujemne, więc abs)
                if (tx.accountNo >= 3000 && tx.accountNo <= 3999) {
                    byProject[key].sales -= tx.amount;
                }
                // Koszty: 4xxx-7xxx (24SO: koszty dodatnie)
                else if (tx.accountNo >= 4000 && tx.accountNo <= 7999) {
                    byProject[key].costs += tx.amount;
                }
            }

            const pnlTxs = transactions.filter(tx => tx.accountNo >= 3000 && tx.accountNo <= 7999);
            const unassigned = pnlTxs.filter(tx => !tx.projectName);
            const allocation = {
                unassignedSales: -unassigned.filter(tx => tx.accountNo < 4000).reduce((s,tx) => s+tx.amount,0),
                unassignedCosts: unassigned.filter(tx => tx.accountNo >= 4000).reduce((s,tx) => s+tx.amount,0),
                unassignedRows: unassigned.filter(tx => Math.abs(tx.amount) > 0.01).length,
            };
            const namesById = new Map();
            for (const tx of pnlTxs) if (tx.projectId && tx.projectName) {
                const id = String(tx.projectId);
                if (!namesById.has(id)) namesById.set(id,new Set());
                namesById.get(id).add(tx.projectName);
            }
            const identityConflicts = [...namesById].filter(([,names]) => names.size > 1)
                .map(([projectId,names]) => ({projectId,names:[...names].sort()})).sort((a,b)=>a.projectId.localeCompare(b.projectId));
            const missingProjectIds=pnlTxs.some(tx=>tx.projectName && !tx.projectId);
            const allocationLimited = allocation.unassignedRows > 0 || identityConflicts.length > 0 || missingProjectIds;
            let allocationNote = allocationLimited ? t('project_pnl_allocation_note',
                Helpers.formatMoney(allocation.unassignedSales), Helpers.formatMoney(allocation.unassignedCosts),
                identityConflicts.map(p => p.projectId + ': ' + p.names.join(' / ')).join('; ') || t('project_pnl_no_conflicts')) : '';
            if(missingProjectIds) allocationNote+=' '+t('project_pnl_missing_ids');
            const projects = Object.values(byProject);
            if (projects.length === 0) return null;

            const projectMonths = new Set(
                transactions
                    .filter(tx => tx.projectId || tx.projectName)
                    .map(tx => String(tx.date || '').slice(0, 7))
                    .filter(m => /^\d{4}-\d{2}$/.test(m))
            );
            const isShortProjectPeriod = projectMonths.size > 0 && projectMonths.size < 3;

            // Tylko projekty z aktywnoscia (>=10 transakcji LUB >=50k saldo)
            // — male projekty (np. testowe) nie powinny zaśmiecać raportu
            const significant = projects.filter(p => p.txCount >= 10 || (Math.abs(p.costs) + Math.abs(p.sales)) >= 50000);
            if (significant.length === 0) return null;

            // Net per projekt = sales - costs. Net < 0 = strata.
            const enriched = significant.map(p => ({
                ...p,
                net: p.sales - p.costs,
            }));

            const losing = enriched.filter(p => p.net < 0).sort((a, b) => a.net - b.net);
            const profitable = enriched.filter(p => p.net >= 0);

            // 11.10.2026 (decyzja ownera, Knu Bygg: 157 wierszy na karcie): karta dalej pokazuje
            // SUMY per projekt, ale tylko projekty wymagające uwagi: na minusie i rozpoczęte w tym
            // roku ALBO z obrotem nad progiem 5G. Projekt z poprzedniego roku na minusie w tym
            // okresie to zwykle fakturowanie w innym roku, nie błąd. Które projekty istniały rok
            // temu, mówi config.priorYearProjects (src/ui/lib/priorYearProjects.js); bez tej listy
            // nie da się ich odróżnić, więc wtedy pokazujemy wszystkie i piszemy to w opisie.
            const threshold = (config && config.prosjektregnskapThreshold) || PROSJEKTREGNSKAP_5G;
            const projectKey = (n) => String(n || '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
            const prior = config?.priorYearProjects;
            const priorNames = prior?.status === 'available' && Array.isArray(prior.names)
                ? new Set(prior.names.map(projectKey)) : null;
            const priorIds = prior?.status==='available' && Array.isArray(prior.projects)
                ? new Set(prior.projects.filter(p=>p.projectId).map(p=>String(p.projectId))) : null;
            const duplicateName = p => projects.filter(other=>projectKey(other.name)===projectKey(p.name)).length>1;
            const priorKnown = p => p.projectId ? !!priorIds && (prior.identityComplete!==false || priorIds.has(p.projectId)) : !!priorNames && !duplicateName(p);
            const startedThisYear = p => p.projectId ? !priorIds || !priorIds.has(p.projectId)
                : !priorNames || !priorKnown(p) || !priorNames.has(projectKey(p.name));
            const projectLabel=p=>duplicateName(p) && p.projectId ? p.name+' (ID '+p.projectId+')' : p.name;
            // „Over terskel" jak w prosjektregnskap_terskel: równe progowi nie wystarcza.
            const inScope = (p) => startedThisYear(p) || p.sales > threshold;
            const shownLosing = losing.filter(inScope);
            const omittedPrior = losing.length - shownLosing.length;
            const MAX_ROWS = 20;

            const row = (p) => ({
                accNum: 0,
                name: `${projectLabel(p)} | ${t('project_pnl_costs')}: ${Helpers.formatMoney(p.costs)} | ${t('project_pnl_sales')}: ${Helpers.formatMoney(p.sales)} | ${t('project_pnl_net')}: ${Helpers.formatMoney(p.net)}${p.net < 0 && !allocationLimited ? ' ⚠️' : ''}${p.firstDate && p.lastDate ? ` | ${t('project_pnl_span', isoToDotDate(p.firstDate), isoToDotDate(p.lastDate))}` : ''}${priorNames && !startedThisYear(p) ? ` | ${t('project_pnl_from_prior_year', prior.year)}` : ''}`,
                accName: projectLabel(p), projectKey: p.key, projectId:p.projectId, costs: p.costs, sales: p.sales, txCount: p.txCount,
                startedThisYear: priorKnown(p) ? startedThisYear(p) : null,
                ub: p.net,
            });
            const sorted = [...enriched].sort((a, b) => a.net - b.net);
            // Pełna lista (także na plusie) zostaje w dowodach, nie na karcie. Dowody wchodzą do
            // klucza potwierdzeń (src/shared/finding-ack.cjs), więc bez tekstów w języku UI i bez
            // stanu pobrania poprzedniego roku: ten idzie do sourceContext, którego klucz nie liczy.
            const evidence = {
                projects: sorted.map(p => ({ projectKey: p.key, projectId:p.projectId, costs: p.costs, sales: p.sales, net: p.net, txCount: p.txCount })),
                threshold, allocation, identityConflicts, missingProjectIds,
            };
            const sourceContext = { priorYear: prior?.status || 'unavailable', priorYearReason: prior?.reason || null, omittedPrior };

            if (losing.length === 0) {
                return {
                    type: allocationLimited ? 'info' : 'success',
                    title: t('rule_project_pnl'),
                    desc: t('rule_project_pnl_ok', enriched.length) + ' ' + allocationNote,
                    details: sorted.slice(0, MAX_ROWS).map(row),
                    specialType: allocationLimited ? undefined : 'tx_passed',
                    evidence, sourceContext,
                };
            }

            const totalLoss = losing.reduce((s, p) => s + p.net, 0);
            const shownLoss = shownLosing.reduce((s, p) => s + p.net, 0);
            const baseDesc = isShortProjectPeriod
                ? t('rule_project_pnl_partial_desc', losing.length, enriched.length, Helpers.formatMoney(totalLoss))
                : t('rule_project_pnl_desc', losing.length, enriched.length, Helpers.formatMoney(totalLoss));
            const listed = Math.min(shownLosing.length, MAX_ROWS);
            const scopeNote = priorNames
                ? t('project_pnl_scope_note', shownLosing.length, listed, omittedPrior, prior.year ?? '', profitable.length, Helpers.formatMoney(threshold))
                : `${t('project_pnl_scope_note_unknown', shownLosing.length, listed, profitable.length)} ${t('project_pnl_prior_unknown')}`;
            if (shownLosing.length === 0) {
                // Wszystkie projekty na minusie są z poprzedniego roku i pod progiem: nic do zrobienia
                // w tym okresie, ale opis mówi, co zostało pominięte.
                return {
                    type: allocationLimited ? 'info' : 'success',
                    title: t('rule_project_pnl'),
                    desc: `${baseDesc} ${allocationNote} ${scopeNote}`,
                    details: [],
                    specialType: allocationLimited ? undefined : 'tx_passed',
                    evidence, sourceContext,
                };
            }
            return {
                type: (isShortProjectPeriod || allocationLimited) ? 'info' : (shownLosing.length >= 3 || Math.abs(shownLoss) >= 500000 ? 'warning' : 'info'),
                title: t('rule_project_pnl'),
                desc: `${baseDesc} ${allocationNote} ${scopeNote}`,
                details: shownLosing.slice(0, MAX_ROWS).map(row),
                evidence, sourceContext,
                why: t('project_pnl_why'),
                verified: [priorNames ? t('project_pnl_prior_checked', prior.year ?? '') : t('project_pnl_prior_unknown'), t('project_pnl_span_note')],
                confidence: 'open',
            };
        }
    },

    // === YoY RULES (v18) — porównanie z previousYear ===
    {
        id: 'yoy_equity_decline',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear) => {
            if (!previousYear || !previousYear.saldo || previousYear.saldo.length === 0) return null;
            // Kapitał własny jako rezyduum: aktywa minus zobowiązania (salda ze znakiem, zobowiązania
            // ujemne). Suma kont 2000-2099 nie zawiera wyniku okresu, a suma wartości bezwzględnych
            // liczyła stratę na 2050 jako wzrost kapitału. Wzór: RAPPORTER 90d8aaa2 (BUG-8).
            const equity = (data) => data
                .filter(r => (r.accNum >= 1000 && r.accNum <= 1999) || (r.accNum >= 2100 && r.accNum <= 2999))
                .reduce((s, r) => s + (r.ub || 0), 0);
            const curr = equity(saldo);
            const prev = equity(previousYear.saldo);
            if (prev < 100) return null;
            // Próg na wartości niezaokrąglonej: spadek o 50,4% zaokrąglał się do 50 i przechodził.
            const change = (curr - prev) / prev * 100;
            if (change < -50) return { type: 'info', title: t('rule_yoy_equity_decline'), desc: t('rule_yoy_equity_decline_desc', Helpers.formatMoney(curr), Helpers.formatMoney(prev), Math.round(change)), specialType: 'health' };
            return null;
        }
    },
    {
        id: 'yoy_debt_increase',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear) => {
            if (!previousYear || !previousYear.saldo || previousYear.saldo.length === 0) return null;
            const sumRange = (data, from, to) => data.filter(r => r.accNum >= from && r.accNum <= to).reduce((s, r) => s + Math.max(0, -(r.ub || 0)), 0);
            const curr = sumRange(saldo, 2200, 2999);
            const prev = sumRange(previousYear.saldo, 2200, 2999);
            if (prev < 1000) return null;
            const pct = Math.round((curr - prev) / prev * 100);
            const period = resolvePeriod(config);
            const currentDate = period ? periodEndDate(period) : t('period_end_generic');
            const previousDate = period && previousYear.saldoEndMonth
                ? periodEndDate({ year: period.year - 1, monthTo: previousYear.saldoEndMonth }) : t('previous_balance_date_unknown');
            if (pct > 50) return { type: 'info', title: t('rule_yoy_debt_increase'), desc: t('rule_yoy_debt_increase_desc', Helpers.formatMoney(curr), Helpers.formatMoney(prev), pct) + `\n${currentDate} / ${previousDate}`, specialType: 'health', evidence: { curr, prev, currentDate, previousDate } };
            return null;
        }
    },

    // === FAZA 7: Nowe reguły analityczne (v39) ===

    // --- Banksaldo per måned — ujemne saldo lub dramatyczny spadek ---
    // GLOBAL-06: cash movements are not an accounting control. Negative-balance checks remain.

    {
        id: 'reverse_periodisering',
        category: 'analysis',
        systems: 'all',
        requires: 'both',
        check: (saldo, resultat, config) => {
            // Check if account 3009 exists in resultat with positive sum (debit = reversed income)
            const acc3009 = resultat.find(r => r.accNum === 3009);
            const acc1530 = saldo.find(a => a.accNum === 1530);
            if (!acc3009 && !acc1530) return null;
            // Flag if 3009 has significant positive (debit) activity
            const has3009Issue = acc3009 && acc3009.sum > 1000;
            // Flag if 1530 exists with non-zero balance
            const has1530Issue = acc1530 && Math.abs(acc1530.ub) > 1000;
            if (!has3009Issue && !has1530Issue) return null;
            const details = [];
            if (acc3009) details.push({ accNum: 3009, name: acc3009.name || 'Påløpt inntekt', accName: acc3009.accName || acc3009.name, sum: acc3009.sum, months: acc3009.months });
            if (acc1530) details.push(acc1530);
            // Kontrakt karty (Knu 11.10.2026, „odwrócona periodyzacja, nie wiem, co to znaczy"):
            // opis po ludzku z kwotami i datą; 1531 o przeciwnym saldzie = odwrócenie na złym koncie.
            // Lustro idzie tylko do „sprawdziłem", nie do details: details są hashowane w kluczu
            // potwierdzeń i dopisany wiersz unieważniałby istniejące ack-i (Codex P2, 11.10.2026).
            const twin = acc1530 ? saldo.find(b => b.accNum >= 1530 && b.accNum <= 1539 && b.accNum !== 1530 && Math.abs((b.ub || 0) + acc1530.ub) <= 1 && Math.abs(acc1530.ub) > 1000) : null;
            const period = resolvePeriod(config);
            return {
                type: 'info', title: t('rule_reverse_period'),
                desc: t('rule_reverse_period_desc_plain', acc3009 ? Helpers.formatMoney(acc3009.sum) : t('no_data'), acc1530 ? Helpers.formatMoney(acc1530.ub) : t('no_data'), period ? periodEndDate(period) : t('period_end_generic')),
                details, source: 'both',
                why: t('reverse_period_why'),
                verified: [twin
                    ? t('reverse_period_pair', twin.accNum, `${acc1530.ub >= 0 ? '+' : '−'}${Helpers.formatMoney(Math.abs(acc1530.ub))}`, `${twin.ub >= 0 ? '+' : '−'}${Helpers.formatMoney(Math.abs(twin.ub))}`)
                    : t('reverse_period_no_pair')],
                // Zgodność kwot to przesłanka, nie dowód (Codex P2): pozostaje „do wyjaśnienia".
                confidence: twin ? 'open' : 'info',
            };
        }
    },

    // === v40: ciągłość IB/UB między latami (inspiracja regnskapskjerne/opening.rs, Thomas/Datamynt) ===
    // IB(rok N+1) musi = UB(rok N) dla KONT BILANSOWYCH (1000-2999). Konta resultat (3xxx+)
    // zerują IB co rok, więc je pomijamy (inaczej fałszywe alarmy). System kopiuje UB→IB,
    // więc każda różnica = błąd ciągłości (ręczne IB, księgowanie w zamknięty rok, zła OB).
    {
        id: 'yoy_ib_ub_continuity',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear) => {
            if (!previousYear || !previousYear.saldo || previousYear.saldo.length === 0) return null;
            const isBalance = (n) => n >= 1000 && n < 3000;
            const prevByAcc = new Map();
            for (const a of previousYear.saldo) {
                if (!isBalance(a.accNum)) continue;
                prevByAcc.set(a.accNum, a);
            }
            if (prevByAcc.size === 0) return null;
            // Samoobrona 1: poprzedni rok bez sensownych UB (pierwszy rok / dane bez historii,
            // wszystkie ub=0) → nie ma do czego porównać → milcz zamiast masowych fałszywych breaks.
            const anyPrevUb = [...prevByAcc.values()].some(a => Math.abs(a.ub || 0) > 1);
            if (!anyPrevUb) return null;
            // Samoobrona 2: eksport bieżący NIE ma kolumny IB (wszystkie ib=0 mimo sald) → milcz.
            const currBalance = saldo.filter(a => isBalance(a.accNum));
            const anyIb = currBalance.some(a => Math.abs(a.ib || 0) > 1);
            if (!anyIb) return null;
            const TOL = 1; // 1 kr — UB→IB to kopia, każda realna różnica jest > øre
            const breaks = [];
            const seen = new Set();
            for (const a of saldo) {
                if (!isBalance(a.accNum)) continue;
                seen.add(a.accNum);
                const prev = prevByAcc.get(a.accNum);
                const prevUb = prev ? (prev.ub || 0) : 0;
                const currIb = a.ib || 0;
                const diff = currIb - prevUb;
                if (Math.abs(diff) > TOL) {
                    breaks.push({
                        accNum: a.accNum,
                        name: `${a.accNum} ${a.name || (prev && prev.name) || ''}`,
                        accName: a.name || '',
                        currentIb: currIb,
                        previousUb: prevUb,
                        diff,
                        ub: diff,
                    });
                }
            }
            // Konta z UB≠0 w poprzednim roku, których brak w bieżącym IB → ciągłość urwana
            for (const [accNum, prev] of prevByAcc) {
                if (seen.has(accNum)) continue;
                if (Math.abs(prev.ub || 0) > TOL) {
                    const prevUb = prev.ub || 0;
                    breaks.push({
                        accNum,
                        name: `${accNum} ${prev.name || ''}`,
                        accName: prev.name || '',
                        currentIb: 0,
                        previousUb: prevUb,
                        diff: -prevUb,
                        ub: -prevUb,
                    });
                }
            }
            if (breaks.length === 0) return null;
            breaks.sort((a, b) => Math.abs(b.ub) - Math.abs(a.ub));
            const total = breaks.reduce((s, b) => s + Math.abs(b.ub), 0);
            return {
                type: 'warning',
                title: t('rule_ib_ub_continuity'),
                desc: t('rule_ib_ub_continuity_desc', breaks.length, Helpers.formatMoney(total)),
                details: breaks.slice(0, 20), evidence: breaks,
                source: 'saldo',
            };
        }
    },

    // === Rok poprzedni nie domknięty (audyt sald 24.09.2026) ===
    // Po zaksięgowaniu dyspozycji wyniku (88xx-89xx) suma kont 3000-8999 za PEŁNY rok musi być
    // zerem. Reszta znaczy, że journal roczny nie domyka własnego wyniku: zła kwota, zła strona,
    // journal dwa razy albo koszty dopisane po nim. Reguła podaje fakt i kwotę, przyczyny nie zgaduje.
    // MILCZY bez dyspozycji: brak journala rocznego księgowi uznają za stan normalny
    // (uwagi z produkcji 06.2026, m.in. firmy księgowane raz w roku).
    // MILCZY bez danych pełnego roku: rachunek wyniku za ten sam okres co audyt nie zawiera
    // zapisów z 31.12, więc nic nie dowodzi.
    {
        id: 'prior_year_not_closed',
        category: 'balance',
        systems: 'all',
        requires: 'saldo',
        check: (saldo, resultat, config, previousYear) => {
            const rows = previousYear?.resultatFullYear;
            if (!Array.isArray(rows) || rows.length === 0) return notChecked('prior_year_check_title', 'prior_year_data_missing');
            const sumRange = (from, to) => rows
                .filter(r => r.accNum >= from && r.accNum <= to)
                .reduce((s, r) => s + (Number(r.sum) || 0), 0);
            const disposition = sumRange(8800, 8999);
            if (Math.abs(disposition) <= 1) return null;
            // Dyspozycja poza grudniem znaczy rok obrotowy inny niż kalendarzowy: rok kalendarzowy
            // nie sumuje się wtedy do zera nawet przy poprawnych księgach, więc nie oceniamy.
            const postedOutsideDecember = rows.some(r => r.accNum >= 8800 && r.accNum <= 8999
                && Object.entries(r.months || {}).some(([m, v]) => m !== 'Des' && Math.abs(Number(v) || 0) > 1));
            if (postedOutsideDecember) return null;
            const remainder = sumRange(3000, 8999);
            if (Math.abs(remainder) <= PRIOR_YEAR_REMAINDER_TOL_KR) return null;
            // Pojedynczy zapis na 89xx to jeszcze nie journal roczny. Gm Renovation 2025: wynik
            // 137 324, na 8990 tylko 9 900 (7%), rozliczenie roczne niezrobione, więc nie błąd
            // (werdykt Patrycji 25.09.2026). Potwierdzone błędy mają dyspozycję od 50% wyniku w górę.
            const resultBeforeDisposition = remainder - disposition;
            if (Math.abs(disposition) < PRIOR_YEAR_MIN_DISPOSITION_SHARE * Math.abs(resultBeforeDisposition)) return null;
            return {
                type: 'warning',
                title: t('rule_prior_year_not_closed'),
                desc: t('rule_prior_year_not_closed_desc', Helpers.formatMoney(remainder), Helpers.formatMoney(disposition)),
                details: [{
                    id: 'prior-year-remainder', accNum: 8960,
                    name: t('prior_year_remainder_row'), accName: t('prior_year_remainder_row'),
                    ib: 0, change: remainder, ub: remainder, disposition,
                }],
                source: 'saldo',
            };
        }
    },

    // === v40: integralność importu — zbalansowany bilag sumuje się do 0 (inspiracja reconciliation.rs) ===
    // Kwoty 24SO są ZE ZNAKIEM (sprzedaż 3xxx ujemna, koszt 4-7xxx dodatni), więc każdy poprawnie
    // zaimportowany bilag sumuje się do ~0. Bilag ≠ 0 = parser zgubił/zdublował linię.
    // SAMOOBRONA: jeśli >30% wielolinijkowych bilagów się nie spina → eksport ma INNĄ konwencję
    // znaku (nie "suma=0") → reguła milczy zamiast spamować. Zero fałszywych alarmów.
    {
        id: 'import_integrity',
        category: 'balance',
        systems: ['24seven'],
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!transactions || transactions.length === 0) return null;
            const unavailable = missingVoucherFinding(transactions, 'rule_import_integrity');
            if (unavailable) return unavailable;
            // Some Finago periodisations split one document over transaction types.
            // Require shared document identity and unique contiguous line numbers; never merge by LinkId.
            const candidates = new Map();
            for (const tx of transactions) {
                if (!(Number(tx.transactionNo) > 0 && tx.stampNo)) continue;
                const key = JSON.stringify([String(tx.date || '').slice(0,4), String(tx.transactionNo), String(tx.stampNo)]);
                if (!candidates.has(key)) candidates.set(key, []);
                candidates.get(key).push(tx);
            }
            const documentKeys = new Map();
            for (const [key, lines] of candidates) {
                const seq = lines.map(tx => Number(tx.sequenceNo)).sort((a,b) => a-b);
                if (lines.some(tx => !tx.invoiceNo || !tx.customerId)
                    || new Set(lines.map(tx => String(tx.transactionTypeId))).size < 2
                    || new Set(lines.map(tx => String(tx.invoiceNo))).size !== 1
                    || new Set(lines.map(tx => String(tx.customerId))).size !== 1
                    || !seq.every((n,i) => n === i+1)) continue;
                for (const tx of lines) documentKeys.set(tx, 'document:' + key);
            }
            const bundles = {};
            let grouped = 0;
            for (const tx of transactions) {
                // UWAGA: grupujemy po stampNo||bundleId, NIE po linkId — linkId w 24SO łączy
                // POWIĄZANE dokumenty (faktura+zapłata), nie linie jednego bilagu. Łączenie po
                // linkId scaliłoby dwa osobne bilagi i zepsuło test sumy=0. Brak stampNo/bundleId
                // → tx pomijany (rule milczy zamiast fałszywie alarmować).
                const bid = documentKeys.get(tx) || voucherKey(tx);
                if (!bid) continue;
                if (!bundles[bid]) bundles[bid] = [];
                bundles[bid].push(tx);
                grouped++;
            }
            // Self-detection: eksport bez ID bilagu → nie da się ocenić double-entry, pomiń
            if (grouped < transactions.length * 0.8) return null;
            const multiline = Object.keys(bundles).filter(b => bundles[b].length >= 2);
            if (multiline.length < 5) return {
                type: 'info', title: t('rule_import_integrity'), desc: t('voucher_sample_small'),
                details: [], specialType: 'rule_not_checked',
            };
            const TOL = IMPORT_INTEGRITY_TOL_KR; // 50 kr — drobne agio/zaokrąglenia nie są alarmem
            const unbalanced = [];
            for (const bid of multiline) {
                const txs = bundles[bid];
                const sum = txs.reduce((s, tx) => s + (tx.amount || 0), 0);
                if (Math.abs(sum) > TOL) {
                    const anyTx = txs[0];
                    const refs = [];
                    if (anyTx && anyTx.stampNo) refs.push(`${t('tx_stempel')} ${anyTx.stampNo}`);
                    if (anyTx && anyTx.linkId) refs.push(`${t('tx_linkid')} ${anyTx.linkId}`);
                    if (anyTx && anyTx.invoiceNo) refs.push(`${t('tx_faktura')} ${anyTx.invoiceNo}`);
                    const accs = [...new Set(txs.map(x => x.accountNo))].join(', ');
                    unbalanced.push({
                        voucherId: bid, date: anyTx?.date, accounts: [...new Set(txs.map(x => x.accountNo))].sort(),
                        accNum: (anyTx && anyTx.accountNo) || 0,
                        name: `${(anyTx && anyTx.date) || '?'} | ${refs.join(' | ') || `${t('tx_id')} ${bid}`} | ${t('tx_konto')} ${accs}`,
                        accName: [...new Set(txs.map(x => x.description).filter(Boolean))].join('; '),
                        ub: sum,
                    });
                }
            }
            // SAMOOBRONA przed złą konwencją znaku eksportu
            if (unbalanced.length / multiline.length > 0.30) return {
                type: 'info', title: t('rule_import_integrity'), desc: t('voucher_convention_unknown'),
                details: [], specialType: 'rule_not_checked',
            };
            if (unbalanced.length === 0) {
                return { type: 'success', title: t('rule_import_integrity'), desc: t('rule_import_integrity_ok', multiline.length), details: [], specialType: 'tx_passed' };
            }
            unbalanced.sort((a, b) => Math.abs(b.ub) - Math.abs(a.ub));
            return {
                type: 'warning',
                title: t('rule_import_integrity'),
                desc: t('rule_import_integrity_desc', unbalanced.length, multiline.length),
                details: unbalanced.slice(0, 20), evidence: unbalanced,
            };
        }
    },

    // === v40: prosjektregnskap-terskel (Patrycja) — punkt kontrolny, NIE wyrok ===
    // §8-1-3 bokføringsforskriften: prosjektregnskap wymagane PER PROJEKT > 5G eks mva,
    // TYLKO bygge-/anleggsvirksomhet. AUDYTOR nie zna NACE, więc to sygnał "sjekk manuelt".
    // Mocny sygnał = duży klient > 5G + projekty NIE założone (zero prosjektføring).
    {
        id: 'prosjektregnskap_terskel',
        category: 'compliance',
        systems: ['24seven', 'tripletex'],
        requires: 'transactions',
        check: (saldo, resultat, config, previousYear, vatMonthly, transactions) => {
            if (!transactions || transactions.length === 0) return null;
            const THRESHOLD = (config && config.prosjektregnskapThreshold) || PROSJEKTREGNSKAP_5G;
            // 1. Wymiar Projekt może występować na kosztach LUB sprzedaży.
            const costTxs = transactions.filter(tx => tx.accountNo >= 4000 && tx.accountNo <= 7999);
            const withProjectCost = costTxs.filter(tx => tx.projectId || tx.projectName).length;
            // 2. Przychód per klient i per projekt (3xxx, 24SO sprzedaż ujemna → abs)
            const salesTxs = transactions.filter(tx => tx.accountNo >= 3000 && tx.accountNo <= 3999);
            if (salesTxs.length === 0) return null;
            const withProjectSales = salesTxs.filter(tx => tx.projectId || tx.projectName).length;
            const usesProjects = (costTxs.length > 0 && withProjectCost / costTxs.length >= 0.20)
                || withProjectSales / salesTxs.length >= 0.20;
            const sumBy = (keyFn) => {
                const m = new Map();
                for (const tx of salesTxs) {
                    const k = keyFn(tx);
                    if (k === null || k === undefined || k === '') continue;
                    m.set(k, (m.get(k) || 0) - (tx.amount || 0));
                }
                return m;
            };
            const byProject = sumBy(tx => tx.projectName || tx.projectId);
            const byCustomer = sumBy(tx => tx.customerName || tx.customerId);
            const bigProjects = [...byProject.entries()].filter(([, v]) => v > THRESHOLD).sort((a, b) => b[1] - a[1]);
            const bigCustomers = [...byCustomer.entries()].filter(([, v]) => v > THRESHOLD).sort((a, b) => b[1] - a[1]);
            if (bigProjects.length === 0 && bigCustomers.length === 0) return null;
            const projectRows = bigProjects.map(([name, sum]) => ({ accNum: 0, name: `${t('prosjektregnskap_project')}: ${name} — ${Helpers.formatMoney(sum)}`, accName: String(name), ub: sum }));
            const customerRows = bigCustomers.map(([name, sum]) => ({ accNum: 0, name: `${t('prosjektregnskap_customer')}: ${name} — ${Helpers.formatMoney(sum)}`, accName: String(name), ub: sum }));
            // Mocny sygnał: duży klient ALE projekty nie założone → możliwy brak prosjektregnskap.
            // Tu wiersze to klienci, bo projektów nie ma.
            if (!usesProjects && bigCustomers.length > 0) {
                return {
                    type: 'warning',
                    title: t('rule_prosjektregnskap'),
                    desc: t('rule_prosjektregnskap_missing_desc', bigCustomers.length, Helpers.formatMoney(THRESHOLD)),
                    details: customerRows.slice(0, 30), evidence: { projects: bigProjects, customers: bigCustomers, usesProjects, threshold: THRESHOLD },
                    source: 'transactions',
                };
            }
            // Projekty założone lub duże projekty wykryte → przypomnienie weryfikacyjne.
            // 11.10.2026 (decyzja ownera): na karcie tylko projekty, klienci zostają liczbą w opisie
            // (Knu Bygg: 15 projektów + 8 klientów = 23 wiersze szumu w sekcji informacyjnej).
            return {
                type: 'info',
                title: t('rule_prosjektregnskap'),
                desc: t('rule_prosjektregnskap_check_desc', bigProjects.length, bigCustomers.length, Helpers.formatMoney(THRESHOLD)),
                details: projectRows.slice(0, 30), evidence: { projects: bigProjects, customers: bigCustomers, usesProjects, threshold: THRESHOLD },
                source: 'transactions',
            };
        }
    },
];

// --- CATEGORY WEIGHTS (Piramida Wartości) ---
export const CATEGORY_WEIGHTS: Record<string, number> = { compliance: 2.0, balance: 1.5, financial: 1.0, result: 1.0, analysis: 0.8 };

// --- SCORING V2 (penalty-based, opt-in via env SCORING_VERSION=v2) ---
// Spec: PROJEKTY/AUDYTOR/SPEC-scoring-v2.md
// Dry-run: __tests__/scoring-v2-dryrun.test.ts (7/7 + 2 real CSV firms PASS)
export const SCORING_V2_PENALTY = {
    critical_compliance: 1.5, // error w kategorii compliance (MVA/AGA/skattetrekk)
    error: 0.7,
    warning: 0.1,
    info: 0,
};

export const SCORING_V2_PER_RULE_CAP = {
    critical_compliance: 4.5, // max 3 findings × 1.5
    error: 4.2,               // max 6 findings × 0.7
    warning: 1.5,             // max 15 findings × 0.1 — chroni przed spam-rule nuke
};

export interface ScoreV2Breakdown {
    score: number;
    totalPenalty: number;
    perRule: Array<{ ruleId: string; bucket: string; penalty: number; findings: number }>;
}

export const scoreV2 = (
    issues: AuditFinding[],
    _passedRules: { ruleId: string; category: string }[],
): ScoreV2Breakdown => {
    const perRulePenalty: Record<string, { penalty: number; findings: number; bucket: string; ruleId: string }> = {};

    for (const issue of issues) {
        if (['financial_result', 'scoring', 'health'].includes(issue.specialType as string)) continue;

        let bucket: keyof typeof SCORING_V2_PENALTY;
        if (issue.type === 'error' && issue.category === 'compliance') bucket = 'critical_compliance';
        else if (issue.type === 'error') bucket = 'error';
        else if (issue.type === 'warning') bucket = 'warning';
        else bucket = 'info';

        if (SCORING_V2_PENALTY[bucket] === 0) continue;

        const ruleId = issue.ruleId || 'unknown';
        const key = `${ruleId}::${bucket}`;
        if (!perRulePenalty[key]) {
            perRulePenalty[key] = { penalty: 0, findings: 0, bucket, ruleId };
        }
        perRulePenalty[key].penalty += SCORING_V2_PENALTY[bucket];
        perRulePenalty[key].findings += 1;

        const cap = SCORING_V2_PER_RULE_CAP[bucket as keyof typeof SCORING_V2_PER_RULE_CAP];
        if (cap && perRulePenalty[key].penalty > cap) perRulePenalty[key].penalty = cap;
    }

    const perRule = Object.values(perRulePenalty).map(p => ({
        ruleId: p.ruleId,
        bucket: p.bucket,
        penalty: Math.round(p.penalty * 100) / 100,
        findings: p.findings,
    })).sort((a, b) => b.penalty - a.penalty);

    const totalPenalty = perRule.reduce((sum, p) => sum + p.penalty, 0);
    const score = Math.max(0, Math.min(10, Math.round((10 - totalPenalty) * 10) / 10));

    return { score, totalPenalty: Math.round(totalPenalty * 100) / 100, perRule };
};

// --- AUDIT RUNNER ---
// Preparation can carry source/lifecycle controls without an executable rule.
// Preserve those in report coverage too, once per ID, in both runners.
const uncheckedCategory = (id: string) => AUDIT_RULES.find(rule => rule.id === id)?.category || 'compliance';
const appendUnmatchedControls = (controls, notCheckedRules, issues) => {
    const recorded = new Set(notCheckedRules.map(rule => rule.ruleId));
    for (const control of controls || []) {
        if (recorded.has(control.id)) continue;
        recorded.add(control.id);
        notCheckedRules.push({ ruleId: control.id, reason: control.reason });
        issues.push({ ...notChecked(RULE_LABELS[control.id] || control.id, control.reason),
            ruleId: control.id, category: uncheckedCategory(control.id),
            evidence: { source: 'audit-preparation', reason: control.reason } });
    }
};

export const runAudit = (
    system: string,
    saldo: AccountRow[],
    resultat: ResultRow[],
    disabledRules: string[] = [],
    previousYear: PreviousYearData | null = null,
    vatMonthly: VatMonthlyRow[] = [],
    transactions: TransactionRow[] = [],
    txError: string | null = null,
    auditMeta: Record<string, any> | null = null,
): AuditResult => {
    const issues: AuditFinding[] = [];
    const passedRules: { ruleId: string; category: string }[] = [];
    const notCheckedRules: { ruleId: string; reason: string }[] = [];
    // Capture once at the runner boundary; rules never consult the wall clock.
    // A caller replaying an audit can supply its original YYYY-MM-DD explicitly.
    const config = { ...SYSTEMS[system], auditDate: new Date().toISOString().slice(0, 10), ...(auditMeta || {}) };

    // Track if transaction-based rules were skipped due to missing data
    const noTransactions = !transactions || transactions.length === 0;
    // Bramka kompletności (29.08.2026): niepusta tablica linii nie znaczy, że
    // linie pokrywają księgę. Gdy sumy per konto są niższe niż ruch z TB, harvest
    // jest urwany i reguły liniowe policzyłyby się na kawałku danych, meldując
    // „przeszło". Wtedy pomijamy je tak samo, jak przy całkowitym braku linii.
    const txCoverage = checkTransactionCoverage(saldo, resultat, transactions);
    const txIncomplete = !noTransactions && !txCoverage.ok;
    let txRulesSkipped = 0;
    // Okres dla etykiet dat na kartach: z UI, a z dat transakcji tylko gdy pokrywają księgę
    // (Codex P2, 11.10.2026: urwane transakcje do 15.07 dawałyby „saldo na 31.07").
    config.period = resolvePeriod(config, txIncomplete ? [] : transactions);

    for (const rule of AUDIT_RULES) {
        // Filtruj per system
        if (rule.systems !== 'all' && !rule.systems.includes(system)) continue;
        // Disabled applicable controls remain unchecked, even when their data is missing.
        if (disabledRules.includes(rule.id)) { notCheckedRules.push({ ruleId: rule.id, reason: 'coverage_reason_disabled' }); continue; }
        const unavailable = config.uncheckedControls?.find(control => control.id === rule.id);
        if (unavailable) {
            notCheckedRules.push({ruleId:rule.id,reason:unavailable.reason});
            issues.push({...notChecked(RULE_LABELS[rule.id] || rule.id),ruleId:rule.id,category:rule.category,
                evidence:{source:'audit-preparation',reason:unavailable.reason}});
            continue;
        }
        // Filtruj per dostępne dane
        if (rule.requires === 'saldo' && saldo.length === 0) { notCheckedRules.push({ ruleId: rule.id, reason: 'saldo' }); continue; }
        if (rule.requires === 'resultat' && resultat.length === 0) { notCheckedRules.push({ ruleId: rule.id, reason: 'resultat' }); continue; }
        if (rule.requires === 'both' && (saldo.length === 0 || resultat.length === 0)) { notCheckedRules.push({ ruleId: rule.id, reason: 'both' }); continue; }
        if (rule.requires === 'transactions' && (noTransactions || txIncomplete)) { txRulesSkipped++; notCheckedRules.push({ ruleId: rule.id, reason: 'transactions' }); continue; }

        const result = rule.check(saldo, resultat, config, previousYear, vatMonthly, transactions);
        if (!result) {
            // Rule passed (no issue found)
            passedRules.push({ ruleId: rule.id, category: rule.category });
            continue;
        }

        const results = Array.isArray(result) ? result : [result];
        if (results.some(r => r.specialType === 'rule_not_checked')) notCheckedRules.push({ ruleId: rule.id, reason: results.find(r => r.specialType === 'rule_not_checked')?.reason || 'context' });
        results.forEach(r => {
            if (['duplicate_entries', 'invoice_numbering', 'duplicate_sales_invoices', 'mva_per_voucher'].includes(rule.id) && r.specialType === 'rule_not_checked') return;
            const actionKey = r.actionKey || 'action_' + rule.id;
            const actionText = t(actionKey);
            issues.push({
                evidence: r.evidence,
                type: r.type,
                title: r.title,
                desc: r.desc,
                details: r.details || [],
                source: r.source || rule.requires,
                specialType: r.specialType || null,
                category: rule.category,
                ruleId: rule.id,
                ...(r.actionKey ? { actionKey: r.actionKey } : {}),
                action: (r.type !== 'success' && actionText !== actionKey) ? actionText : null,
                // Język, w którym powstał opis: UI oddaje go wprost, gdy bieg był już w języku
                // widoku, zamiast podmieniać ogólnikiem (11.10.2026, karty PL u Knu Bygg).
                lang: getCurrentLang(),
                ...cardContract(r),
            });
        });
    }

    appendUnmatchedControls(config.uncheckedControls, notCheckedRules, issues);

    // PENALTY-BASED SCORING (single source of truth)
    // Spec: PROJEKTY/AUDYTOR/SPEC-scoring-v2.md
    // 10 - sum(penalty per rule, capped). info=0, warning=-0.1 (cap -1.5), error=-0.7 (cap -4.2), critical compliance=-1.5 (cap -4.5).
    const errCount = issues.filter(i => i.type === 'error' && i.specialType !== 'financial_result').length;
    const warnCount = issues.filter(i => i.type === 'warning').length;
    const infoCount = issues.filter(i => i.type === 'info').length;
    const reviewSignalCount = issues.filter(i => i.specialType === 'review_signal').length;

    const scoreResult = scoreV2(issues, passedRules);
    const score = scoreResult.score;
    const scoreBreakdown = scoreResult.perRule;
    const totalPenalty = scoreResult.totalPenalty;

    // CATEGORY SUMMARY (uses CATEGORY_WEIGHTS for backward-compat UI category pills)
    const categorySummary = {};
    for (const cat of RULE_CATEGORIES) {
        const catIssues = issues.filter(i => i.category === cat.id && !['financial_result','scoring','health','rule_not_checked','review_signal'].includes(i.specialType));
        const catPassed = passedRules.filter(p => p.category === cat.id);
        const catErrors = catIssues.filter(i => i.type === 'error').length;
        const catWarnings = catIssues.filter(i => i.type === 'warning').length;
        const catInfos = catIssues.filter(i => i.type === 'info' || i.type === 'success').length;
        const w = CATEGORY_WEIGHTS[cat.id] || 1.0;
        const catMax = (catErrors + catWarnings + catInfos + catPassed.length) * w;
        const catGot = (catPassed.length * 1.0 + catInfos * 1.0 + catWarnings * 0.5) * w;
        const catScore = catMax > 0 ? Math.round((catGot / catMax) * 100) : 100;
        categorySummary[cat.id] = {
            reviewSignals: issues.filter(i => i.category === cat.id && i.specialType === 'review_signal').length,
            unchecked: notCheckedRules.filter(r => uncheckedCategory(r.ruleId) === cat.id).length,
            errors: catErrors,
            warnings: catWarnings,
            passed: catPassed.length,
            total: catErrors + catWarnings + catInfos + catPassed.length,
            weight: w,
            pct: catScore,
        };
    }

    // Info: transaction-based rules skipped
    if (txRulesSkipped > 0) {
        // Powód pomijania: błąd pobrania albo urwany harvest. Ten drugi jest
        // groźniejszy, bo dane wyglądają na kompletne, więc mówimy go wprost.
        const coverageReason = txIncomplete
            ? t('tx_coverage_gap', txCoverage.gaps.length, Math.round(txCoverage.totalMissing), txCoverage.gaps[0].accNum)
            : null;
        const errDetail = (txError || coverageReason) ? t('tx_skip_reason', txError || coverageReason) : '';
        const txStatus = hasVerifiedEmptyTransactions(auditMeta, transactions, txError, saldo, resultat, vatMonthly)
            ? 'tx_verified_empty' : 'tx_skipped';
        issues.push({
            type: 'info',
            title: t('rule_' + txStatus),
            desc: t('rule_' + txStatus + '_desc') + errDetail,
            details: [], source: 'saldo', specialType: txStatus,
            category: 'compliance', ruleId: txStatus, action: null,
            ...(txStatus === 'tx_verified_empty' ? { evidence: auditMeta.transactionEvidence } : {}),
        });
    }

    notCheckedRules.sort((a, b) => a.ruleId.localeCompare(b.ruleId));
    if (notCheckedRules.length > 0) issues.push({ type: 'info', title: t('audit_coverage_title'), desc: t('audit_coverage_notice', notCheckedRules.length), specialType: 'coverage', details: notCheckedRules, ruleId: 'coverage', action: null });

    // SCORING ISSUE (keep for backwards compat in issues array)
    const disabledCount = notCheckedRules.filter(r => r.reason === 'coverage_reason_disabled').length;
    const scoringDesc = t('rule_scoring_desc', errCount, warnCount, infoCount)
        + (reviewSignalCount ? '\n' + t('audit_review_notice', reviewSignalCount) : '')
        + (notCheckedRules.length ? '\n' + t('audit_coverage_notice', notCheckedRules.length) : '')
        + (disabledCount > 0 ? `\n\n⚠ ${t('label_disabled_rules', disabledCount)}` : '');
    // V2 thresholds: ≥8.5 success, ≥6 warning, <6 error
    issues.push({
        type: score >= 8.5 ? (reviewSignalCount || notCheckedRules.length ? 'info' : 'success') : score >= 6 ? 'warning' : 'error',
        title: t('rule_scoring_title', score),
        desc: scoringDesc,
        details: [], source: 'saldo', specialType: 'scoring',
        category: null, ruleId: null, action: null,
    });

    return { issues, score, scoreBreakdown, totalPenalty, errCount, warnCount, infoCount, categorySummary, passedRules, notCheckedRules, reviewSignalCount };
};

/**
 * Async version of runAudit with per-rule progress callback.
 * Uses microtask yielding (setTimeout 0) to let React re-render between rules.
 *
 * onProgress(ruleIndex, totalApplicable, ruleLabel) is called BEFORE each rule check.
 */
export type AuditProgressCallback = (ruleIndex: number, totalApplicable: number, ruleLabel: string) => void;

export const runAuditAsync = async (
    system: string,
    saldo: AccountRow[],
    resultat: ResultRow[],
    disabledRules: string[] = [],
    previousYear: PreviousYearData | null = null,
    vatMonthly: VatMonthlyRow[] = [],
    transactions: TransactionRow[] = [],
    txError: string | null = null,
    onProgress?: AuditProgressCallback,
    auditMeta: Record<string, any> | null = null,
): Promise<AuditResult> => {
    const issues: AuditFinding[] = [];
    const passedRules: { ruleId: string; category: string }[] = [];
    const notCheckedRules: { ruleId: string; reason: string }[] = [];
    const config = { ...SYSTEMS[system], auditDate: new Date().toISOString().slice(0, 10), ...(auditMeta || {}) };

    const noTransactions = !transactions || transactions.length === 0;
    // Bramka kompletności (29.08.2026): niepusta tablica linii nie znaczy, że
    // linie pokrywają księgę. Gdy sumy per konto są niższe niż ruch z TB, harvest
    // jest urwany i reguły liniowe policzyłyby się na kawałku danych, meldując
    // „przeszło". Wtedy pomijamy je tak samo, jak przy całkowitym braku linii.
    const txCoverage = checkTransactionCoverage(saldo, resultat, transactions);
    const txIncomplete = !noTransactions && !txCoverage.ok;
    let txRulesSkipped = 0;
    config.period = resolvePeriod(config, txIncomplete ? [] : transactions);

    // Pre-filter applicable rules to get accurate total count
    const applicableRules = AUDIT_RULES.filter(rule => {
        if (rule.systems !== 'all' && !rule.systems.includes(system)) return false;
        // Match the synchronous runner: record scope before checking data availability.
        if (disabledRules.includes(rule.id)) { notCheckedRules.push({ ruleId: rule.id, reason: 'coverage_reason_disabled' }); return false; }
        const unavailable = config.uncheckedControls?.find(control => control.id === rule.id);
        if (unavailable) {
            notCheckedRules.push({ruleId:rule.id,reason:unavailable.reason});
            issues.push({...notChecked(RULE_LABELS[rule.id] || rule.id),ruleId:rule.id,category:rule.category,
                evidence:{source:'audit-preparation',reason:unavailable.reason}});
            return false;
        }
        if (rule.requires === 'saldo' && saldo.length === 0) { notCheckedRules.push({ ruleId: rule.id, reason: 'saldo' }); return false; }
        if (rule.requires === 'resultat' && resultat.length === 0) { notCheckedRules.push({ ruleId: rule.id, reason: 'resultat' }); return false; }
        if (rule.requires === 'both' && (saldo.length === 0 || resultat.length === 0)) { notCheckedRules.push({ ruleId: rule.id, reason: 'both' }); return false; }
        // transactions rules stay in — we handle skip inside the loop
        return true;
    });

    const totalApplicable = applicableRules.length;

    for (let i = 0; i < applicableRules.length; i++) {
        const rule = applicableRules[i];

        // Report progress — translate rule label for display
        if (onProgress) {
            const labelKey = RULE_LABELS[rule.id];
            const ruleLabel = labelKey ? t(labelKey) : rule.id;
            onProgress(i, totalApplicable, ruleLabel);
            // Yield to event loop so React can re-render the progress bar
            await new Promise<void>(resolve => setTimeout(resolve, 0));
        }

        if (rule.requires === 'transactions' && (noTransactions || txIncomplete)) { txRulesSkipped++; notCheckedRules.push({ ruleId: rule.id, reason: 'transactions' }); continue; }

        const result = rule.check(saldo, resultat, config, previousYear, vatMonthly, transactions);
        if (!result) {
            passedRules.push({ ruleId: rule.id, category: rule.category });
            continue;
        }

        const results = Array.isArray(result) ? result : [result];
        if (results.some(r => r.specialType === 'rule_not_checked')) notCheckedRules.push({ ruleId: rule.id, reason: results.find(r => r.specialType === 'rule_not_checked')?.reason || 'context' });
        results.forEach(r => {
            if (['duplicate_entries', 'invoice_numbering', 'duplicate_sales_invoices', 'mva_per_voucher'].includes(rule.id) && r.specialType === 'rule_not_checked') return;
            const actionKey = r.actionKey || 'action_' + rule.id;
            const actionText = t(actionKey);
            issues.push({
                evidence: r.evidence,
                type: r.type,
                title: r.title,
                desc: r.desc,
                details: r.details || [],
                source: r.source || rule.requires,
                specialType: r.specialType || null,
                category: rule.category,
                ruleId: rule.id,
                ...(r.actionKey ? { actionKey: r.actionKey } : {}),
                action: (r.type !== 'success' && actionText !== actionKey) ? actionText : null,
                lang: getCurrentLang(),
                ...cardContract(r),
            });
        });
    }

    appendUnmatchedControls(config.uncheckedControls, notCheckedRules, issues);

    // === Scoring (identical to runAudit) ===
    const errCount = issues.filter(i => i.type === 'error' && i.specialType !== 'financial_result').length;
    const warnCount = issues.filter(i => i.type === 'warning').length;
    const infoCount = issues.filter(i => i.type === 'info').length;
    const reviewSignalCount = issues.filter(i => i.specialType === 'review_signal').length;

    const scoreResult = scoreV2(issues, passedRules);
    const score = scoreResult.score;
    const scoreBreakdown = scoreResult.perRule;
    const totalPenalty = scoreResult.totalPenalty;

    const categorySummary = {};
    for (const cat of RULE_CATEGORIES) {
        const catIssues = issues.filter(i => i.category === cat.id && !['financial_result','scoring','health','rule_not_checked','review_signal'].includes(i.specialType));
        const catPassed = passedRules.filter(p => p.category === cat.id);
        const catErrors = catIssues.filter(i => i.type === 'error').length;
        const catWarnings = catIssues.filter(i => i.type === 'warning').length;
        const catInfos = catIssues.filter(i => i.type === 'info' || i.type === 'success').length;
        const w = CATEGORY_WEIGHTS[cat.id] || 1.0;
        const catMax = (catErrors + catWarnings + catInfos + catPassed.length) * w;
        const catGot = (catPassed.length * 1.0 + catInfos * 1.0 + catWarnings * 0.5) * w;
        const catScore = catMax > 0 ? Math.round((catGot / catMax) * 100) : 100;
        categorySummary[cat.id] = {
            reviewSignals: issues.filter(i => i.category === cat.id && i.specialType === 'review_signal').length,
            unchecked: notCheckedRules.filter(r => uncheckedCategory(r.ruleId) === cat.id).length,
            errors: catErrors, warnings: catWarnings, passed: catPassed.length,
            total: catErrors + catWarnings + catInfos + catPassed.length,
            weight: w, pct: catScore,
        };
    }

    if (txRulesSkipped > 0) {
        // Powód pomijania: błąd pobrania albo urwany harvest. Ten drugi jest
        // groźniejszy, bo dane wyglądają na kompletne, więc mówimy go wprost.
        const coverageReason = txIncomplete
            ? t('tx_coverage_gap', txCoverage.gaps.length, Math.round(txCoverage.totalMissing), txCoverage.gaps[0].accNum)
            : null;
        const errDetail = (txError || coverageReason) ? t('tx_skip_reason', txError || coverageReason) : '';
        const txStatus = hasVerifiedEmptyTransactions(auditMeta, transactions, txError, saldo, resultat, vatMonthly)
            ? 'tx_verified_empty' : 'tx_skipped';
        issues.push({
            type: 'info', title: t('rule_' + txStatus),
            desc: t('rule_' + txStatus + '_desc') + errDetail,
            details: [], source: 'saldo', specialType: txStatus,
            category: 'compliance', ruleId: txStatus, action: null,
            ...(txStatus === 'tx_verified_empty' ? { evidence: auditMeta.transactionEvidence } : {}),
        });
    }

    notCheckedRules.sort((a, b) => a.ruleId.localeCompare(b.ruleId));
    if (notCheckedRules.length > 0) issues.push({ type: 'info', title: t('audit_coverage_title'), desc: t('audit_coverage_notice', notCheckedRules.length), specialType: 'coverage', details: notCheckedRules, ruleId: 'coverage', action: null });

    const disabledCount = notCheckedRules.filter(r => r.reason === 'coverage_reason_disabled').length;
    const scoringDesc = t('rule_scoring_desc', errCount, warnCount, infoCount)
        + (reviewSignalCount ? '\n' + t('audit_review_notice', reviewSignalCount) : '')
        + (notCheckedRules.length ? '\n' + t('audit_coverage_notice', notCheckedRules.length) : '')
        + (disabledCount > 0 ? `\n\n⚠ ${t('label_disabled_rules', disabledCount)}` : '');
    issues.push({
        type: score >= 8.5 ? (reviewSignalCount || notCheckedRules.length ? 'info' : 'success') : score >= 6 ? 'warning' : 'error',
        title: t('rule_scoring_title', score),
        desc: scoringDesc,
        details: [], source: 'saldo', specialType: 'scoring',
        category: null, ruleId: null, action: null,
    });

    // Final progress: done
    if (onProgress) {
        onProgress(totalApplicable, totalApplicable, '');
    }

    return { issues, score, scoreBreakdown, totalPenalty, errCount, warnCount, infoCount, categorySummary, passedRules, notCheckedRules, reviewSignalCount };
};

// Expose to window for backward compatibility
Object.assign(window as any, {
    RULE_LABELS, CATEGORY_BADGE_LABELS, CATEGORY_BADGE_COLORS,
    RULE_CATEGORIES, AUDIT_RULES, CATEGORY_WEIGHTS, runAudit, runAuditAsync, scoreV2, SCORING_V2_PENALTY, SCORING_V2_PER_RULE_CAP,
});
